import React, {
  useContext,
  useState,
  useRef,
  useEffect,
  useCallback,
  forwardRef,
  useReducer,
} from "react";

import {
  IconButton,
  Box,
  Button,
  FormControlLabel,
  FormControl,
  Checkbox,
  TextField,
  Autocomplete,
} from "@mui/material";

import AlertNotification from "../components/AlertNotification";
import PdfViewerComponent from "../components/PdfViewerComponent";
import ToggleSplitPane from "../components/ToggleSplitPane";
import PageLoader from "../components/PageLoader";
import { DataGrid, useGridApiRef } from "@mui/x-data-grid";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import ClearIcon from "@mui/icons-material/Clear";
import { DeleteForeverRounded } from "@mui/icons-material";
import CaseInformationPane from "../components/Workflow/CaseInformation";

import {
  CaseWorkflowService,
  TextAreaWithCount,
  TextFieldWithIconButton,
  CustomAutoComplete,
  DocumentUtility,
  UtilityLibrary,
  batchDataService,
  BusinessUtility,
  ApiManager,
  ApiEndpoints,
  DebounceForm,
  ValidateRequiredFieldsInForm,
  ValidateForm,
  sessionService,
  EnumUtility,
  LoggerUtility,
  ConfirmationDialog,
  NotificationDialog,
  DeterminePermissions,
  CaseDetailManager,
  ClientType,
  SharedService,
  SessionManager,
  TimerUtility,
} from "../components/ComponentsLibrary";

import { useConfigContext } from "../functions/ConfigContext";
import { List } from "pspdfkit";

const NewWalkIn = forwardRef((props, ref) => {
  const leftPaneTitle = "New Walk-In";
  const rightPaneTitle = "Case Information";

  const errorFormName = "New Walk-In";
  const caseUniqueID = "caseNumber";

  const { configLibrary } = useConfigContext();
  // Create a ref for the child component
  const caseInfoRef = useRef(null);
  const caseNumberRef = useRef();
  const narrativeForStateRef = useRef();
  const noteForActionRef = useRef();
  const listOfActionsApiRef = useGridApiRef();

  const masterServerDBSchema = SessionManager.GetDataFromSession(
    "SiteSettingsDetailsKey",
  )?.masterDBSchema;
  const {
    siteID,
    stateID,
    legacyUserID: userLoginID,
  } = SessionManager.GetDataFromSession("LoginUserInfoKey") || {};
  // Validation patterns
  const siteSettingDetail = JSON.parse(
    SessionManager.GetValueFromSession(SessionManager.SiteSettingsDetailsKey),
  );
  const timeZone = SessionManager.GetDataFromSession("LoginUserInfoKey")?.timeZone;

  const caseNumberRegex = siteSettingDetail.CaseNumberRegex || {
    caseRegexValue: "",
    caseRegexMessage: "",
  };

  // Notes Character Limit
  const notesCharLimit = SessionManager.GetValueFromSession(SessionManager.NotesCharsLimitKey) || 500;
  const narrativeCharLimit = SessionManager.GetValueFromSession(SessionManager.NarrativeCharsLimitKey) || 500;


  const [notificationDialogOpen, setNotificationDialogOpen] = useState(false);
  const [notificationDialogContent, setNotificationDialogContent] = useState([]);
  const [timerNotificationDialogClose, setTimerNotificationDialogClose] = useState(5000);
  const [timerAutoCloseNotificationDialog, setTimerAutoCloseNotificationDialog] = useState(false);

  // State to track when focus should be set to case search field after notification dialog closes
  const [shouldFocusCaseSearch, setShouldFocusCaseSearch] = useState(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [successMessageText, setSuccessMessageText] = useState("");


  const [externalBatchID, setExtBatchID] = useState(0);
  const [showExtDocGrid, setShowExtDocGrid] = useState(false);

  const initialCaseNumberIconsDisabled = { search: false, copy: false, clear: false };
  const [caseNumberIconsDisabled, setCaseNumberIconsDisabled] = useState(initialCaseNumberIconsDisabled);

  const [leftPaneMessage, setLeftPaneMessage] = useState("");
  const [isPageLoading, setIsPageLoading] = useState(false);

  const [hasListOfActions, setHasListOfActions] = useState(true);
  const [resultsListOfActions, setResultsListOfActions] = useState([]);
  const [lastAddedItemId, setLastAddedItemId] = useState(null);
  const [workflowData, setWorkflowData] = useState(null);

  const [channelRoleCategories, setChannelRoleCategories] = useState([]);
  const [roleOptions, setRoleOptions] = useState([]);
  const [channelOptions, setChannelOptions] = useState([]);
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [subCategoryOptions, setSubCategoryOptions] = useState([]);
  const [resultTypes, setResultTypes] = useState([]);
  const [actionTakenOptions, setActionTakenOptions] = useState([]);
  const [iccOptions, setIccOptions] = useState([]);
  const [resultListMapData, setResultListMapData] = useState([]);
  const [caseSearchMFData, setcaseSearchMFData] = useState({});

  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [confirmationDialogContent, setConfirmationDialogContent] = useState([]);
  
  const [channelRoleCategoriesData, setChannelRoleCategoriesData] = useState([]);
  const [caseValidationType, setCaseValidationType] = useState(EnumUtility.CaseType.Case);

  const [listOfActionsGridIndex, setListOfActionGridIndex] = useState(0);

  const [isEditable, setIsEditable] = useState(false);

  const [areDropdownsLocked, setAreDropdownsLocked] = useState(false);
  const [isCaseConfidentialNoPermission, setIsCaseConfidentialNoPermission] = useState(false);

  const defaultPermissionsForFields = {
    newWalkInBtn: { read: false, write: true, hide: false },
    caseNumberTxt: { read: false, write: true, hide: false },
    noCaseChk: { read: false, write: true, hide: false },
    channelSelect: { read: false, write: true, hide: false },
    roleSelect: { read: false, write: true, hide: false },
    firstNameTxt: { read: false, write: true, hide: false },
    lastNameTxt: { read: false, write: true, hide: false },
    companyNameTxt: { read: false, write: true, hide: false },
    categorySelect: { read: false, write: true, hide: false },
    subCategorySelect: { read: false, write: true, hide: false },
    actionTakenSelect: { read: false, write: true, hide: false },
    iccSelect: { read: false, write: true, hide: false },
    noteForActionTxt: { read: false, write: true, hide: false },
    narrativeForStateTxt: { read: false, write: true, hide: false },
    addToListBtn: { read: false, write: true, hide: false },
    saveBtn: { read: false, write: true, hide: false },
    saveAnotherBtn: { read: false, write: true, hide: false },
    cancelBtn: { read: false, write: true, hide: false },
    skipBtn: { read: false, write: true, hide: false },
    };
  const [formFieldAccess, setFormFieldAccess] = useState(defaultPermissionsForFields);

  const [timerView, setTimerView] = useState("00:00");
  const timerRef = React.useRef(null); // Keep the TimerUtils instance
  const isTimerVisible = false;

  const [walkinStartTime, setWalkinStartTime] = useState(null);
  const [pendingAutoAdd, setPendingAutoAdd] = useState(false);

  // Initialize timer only once
  if (!timerRef.current) {
    timerRef.current = new TimerUtility(setTimerView);
  };

  // Initial state for form fields
  const initialState = {
    caseNumberTxt: false,
    noCaseChk: false,
    channelSelect: false,
    roleSelect: false,
    firstNameTxt: false,
    lastNameTxt: false,
    categorySelect: false,
    subCategorySelect: false,
    actionTakenSelect: false,
    iccSelect: false,
    iccViewEnabled: false,
    addToListBtn: false,
    noteForActionTxt: false,
    narrativeForStateTxt: false,
    saveBtn: false,
    saveAnotherBtn: false,
    skipBtn: false,
  };

  // Reducer to manage enable/disable state
  function reducer(state, action) {
    switch (action.type) {
      case "SET_FIELD_STATE":
        return { ...state, [action.payload.field]: action.payload.isEnabled };
      case "SET_BUTTON_VISIBILITY":
        return { ...state, [action.payload.field]: action.payload.isVisible };
      case "SET_STATE_VARIABLE":
        return {
          ...state,
          hasBatchLocked: action.payload,
        };
      case "SET_FIELD_VISIBILITY":
        return {
          ...state,
          [`${action.payload.field}Visible`]: action.payload.isVisible,
        };
      default:
        throw new Error("Unknown action type");
    }
  }

  const [formState, dispatch] = useReducer(reducer, initialState);

  // Group all init objects in one place
  const initObjects = {
    channel: { id: -123456, name: "" },
    role: { id: -123456, name: "" },
    category: { id: -123456, name: "" },
    subCategory: { id: -123456, name: "" },
    actionTaken: { id: -123456, name: "" },
    icc: { id: -123456, name: "" },
  };

  const initChannelSelectedItem = { channelTypeID: -123456 };//TODO
  const initRoleSelectedItem = { contactRoleID: -123456 };
  const initCategorySelectedItem = { categoryTypeID: -123456 };
  const initSubCategorySelectedItem = { subCategoryTypeID: -123456 };
  const initActionTakenSelectedItem = { resultGroupTypeID: -123456 };
  const initIccSelectedItem = { resultGroupTypeID: -123456 };

  const initialFormValues = {
    caseNumberTxt: "",
    yTracCaseID: 0,
    caseNumberSearched: "",
    noCaseChk: false,
    isNoCase: 0,
    roleSelect: initObjects.role,
    roleSelectedItem: initRoleSelectedItem,
    categorySelect: initObjects.category,
    categorySelectedItem: initCategorySelectedItem,
    subCategorySelect: initObjects.subCategory,
    subCategorySelectedItem: initSubCategorySelectedItem,
    actionTakenSelect: initObjects.actionTaken,
    actionTakenSelectedItem: initActionTakenSelectedItem,
    iccSelect: initObjects.icc,
    iccSelectedItem: initIccSelectedItem,
    iccViewEnabled: false,
    noteForActionTxt: "",
    narrativeForStateTxt: "",
    companyNameTxt: "",
    firstNameTxt: "",
    lastNameTxt: "",
    contactNamesEnabled: false,
    iccEnabled: false,
    tracNoteRequired: false
  };
  const [formValues, setFormValues] = useState(initialFormValues);

  const noteForActionEmpty =
    !formValues.noteForActionTxt || formValues.noteForActionTxt.trim() === "";

  const isSaveEnabled = !(resultsListOfActions.length === 0 ||
    (formValues.noCaseChk && noteForActionEmpty) ||
    (formValues?.tracNoteRequired && noteForActionEmpty)
  );

  const isShortCircuitRoleSelected = !!formValues?.roleSelectedItem?.enableShortCircuit;

  const [isSkipVisible, setIsSkipVisible] = useState(false);

  const simulateAsyncDelay = async () => {
    await new Promise((resolve) => setTimeout(resolve, 0));
  };

  const FormFieldEnableType = {
    PageLoad: { ID: 100, name: "PageLoad" },
    NewWalkInClick: { ID: 200, name: "NewWalkInClick" },
    CaseNumberValid: { ID: 300, name: "CaseNumberValid" },
    CaseNumberInValid: { ID: 400, name: "CaseNumberInValid" },
    NoCaseSelect: { ID: 500, name: "NoCaseSelect" },
    RoleSelect: { ID: 600, name: "RoleSelect" },
    CategorySelect: { ID: 700, name: "CategorySelect" },
    SubCategorySelect: { ID: 800, name: "SubCategorySelect" },
    ActionTakenSelect: { ID: 900, name: "ActionTakenSelect" },
    IccSelect: { ID: 1000, name: "IccSelect" },
    SaveNewWalkIn: { ID: 1100, name: "SaveNewWalkIn" },
    SaveAnother: { ID: 1200, name: "SaveAnother" },
  };

  const [requiredFieldLabels, setRequiredFieldLabels] = useState({
    caseNumberTxt: "",
    noCaseChk: "",
    channelSelect: "",
    roleSelect: "",
    firstNameTxt: "",
    lastNameTxt: "",
    companyNameTxt: "",
    categorySelect: "",
    subCategorySelect: "",
    actionTakenSelect: "",
    iccSelect: "",
    noteForActionTxt: "",
    narrativeForStateTxt: "",
  });


  useEffect(() => {
    const fetchFormFieldPermissions = async () => {
      try {
        //TODO: Pass variables from session to the below URL
        const loginUserInfo = sessionService.getUser();
        const loginStateID = loginUserInfo ? loginUserInfo.stateID : -123456;
        const params = {
          clientID: loginStateID,
          formID: 1      // To-do: Modify Code - Hard-coded value of FormID: 1
        }
        const controlSecurityListApiData = await ApiManager.FetchControlsPermissions(params);
        const actualPermissionsForFields = DeterminePermissions(controlSecurityListApiData);
        setFormFieldAccess({ ...defaultPermissionsForFields, ...actualPermissionsForFields });
      }
      catch (error) {
        await LoggerUtility.ManageErrorLogging(error, errorFormName, "fetchFormFieldPermissions");
      }
    };

    fetchFormFieldPermissions();
}, []);

  const handleRemovActionConfirmationDialog = (primaryID) => {
    setTimeout(() => {
      setResultsListOfActions((prevResults) => {
        const removedItem = prevResults.find((item) => item.id === primaryID);
        const removedWasShortCircuit = !!removedItem?.IsShortCircuit;

        const updatedResults = prevResults.filter((item) => item.id !== primaryID);

        if (removedWasShortCircuit) {
          noteForActionRef.current?.clear();

          setFormValues((prev) => ({
            ...prev,
            caseNumberTxt: "",
            yTracCaseID: 0,
            caseNumberSearched: "",
            noCaseChk: true,
            isNoCase: 1,
            tracNoteRequired: true,
            firstNameTxt: "",
            lastNameTxt: "",
            companyNameTxt: "",
            contactNamesEnabled: true,
            noteForActionTxt: "",
            roleSelect: initObjects.role,
            roleSelectedItem: initRoleSelectedItem,
            categorySelect: initObjects.category,
            categorySelectedItem: initCategorySelectedItem,
            subCategorySelect: initObjects.subCategory,
            subCategorySelectedItem: initSubCategorySelectedItem,
            actionTakenSelect: initObjects.actionTaken,
            actionTakenSelectedItem: initActionTakenSelectedItem,
            iccSelect: initObjects.icc,
            iccSelectedItem: initIccSelectedItem,
            iccViewEnabled: false,
            iccEnabled: false,
          }));

          setCaseValidationType(EnumUtility.CaseType.NoCase);
          clearCaseTabInfo();
          caseNumberRef.current?.setValidationMessage("");

          if (narrativeForStateRef.current) {
            narrativeForStateRef.current.clear();
          }

          setSubCategoryOptions([]);
          setActionTakenOptions([]);
          setIccOptions([]);

          setAreDropdownsLocked(false);

          formFieldsEnableLogic(dispatch, FormFieldEnableType.NoCaseSelect.ID);
          loadChannelRoleCategoryOptions(channelRoleCategoriesData);
        }

        if (updatedResults.length === 0) {
          setLastAddedItemId(null);
          setAreDropdownsLocked(false);
          setPendingAutoAdd(false);

          setFormValues((prev) => {
            const updatedFormValues = {
              ...prev,
              roleSelect: initObjects.role,
              roleSelectedItem: initRoleSelectedItem,
              categorySelect: initObjects.category,
              categorySelectedItem: initCategorySelectedItem,
              subCategorySelect: initObjects.subCategory,
              subCategorySelectedItem: initSubCategorySelectedItem,
              actionTakenSelect: initObjects.actionTaken,
              actionTakenSelectedItem: initActionTakenSelectedItem,
              iccSelect: initObjects.icc,
              iccSelectedItem: initIccSelectedItem,
              iccViewEnabled: false,
              iccEnabled: false,
            };

            const requiredAsterisks = calculateRequiredAsterisks(
              updatedFormValues,
              caseValidationType,
              updatedResults.length
            );

            setRequiredFieldLabels(requiredAsterisks);

            return updatedFormValues;
          });

          setSubCategoryOptions([]);
          setActionTakenOptions([]);
          setIccOptions([]);

          formFieldsEnableLogic(
            dispatch,
            formValues.noCaseChk
              ? FormFieldEnableType.NoCaseSelect.ID
              : FormFieldEnableType.CaseNumberValid.ID
          );

          dispatch({
            type: "SET_FIELD_STATE",
            payload: { field: "addToListBtn", isEnabled: false },
          });
        } else if (lastAddedItemId === primaryID) {
          setLastAddedItemId(updatedResults[updatedResults.length - 1]?.id ?? null);
        }

        return updatedResults;
      });
    }, 0);
  };


  // Data for the document grid in view 2
  const listOfActionsGridColumns = [
    {
      field: 'Action', headerName: 'Queued Action', flex: 1,
      renderCell: (params) => {
        return (
          <div className="d-flex justify-content-start align-items-center" style={{ cursor: "pointer" }}>
            <DeleteForeverRounded index={params.row.id}
              onMouseDown={(event) => {
                event.stopPropagation();
                event.preventDefault();
              }}
              onClick={(event) => {
                event.stopPropagation();
                event.preventDefault();
                handleRemovActionConfirmationDialog(params.row.id);
              }}
              color='ywMainBlue' />
            <div style={{ paddingLeft: "10px" }}>
              {params.row.Action}
            </div>
          </div>
        );
      }
    },
    { field: 'Workflow', headerName: 'Workflow', flex: 1 },
    { field: 'Queue', headerName: 'Queue', flex: 1 },
    { field: 'DaysToNext', headerName: 'Days To Next', flex: 1 },
    { field: 'NextActionType', headerName: 'Next Action Type', flex: 1 },
  ];

  
  const dropdownValidator = (value) => {
    if (!value) return false;

    if (typeof value === "number") {
      return value > 0;
    }

    if (typeof value === "object") {
      return typeof value.id === "number" && value.id > 0;
    }

    return false;
  };

  const getCaseValidationRules = (values) => {

    let caseNumberValidation = { required: false };
    //TODO check if needed
    if (siteSettingDetail.CaseNumberInsertAllow === "1") {
      caseNumberValidation = { required: true, validator: () => values.caseNumberSearched !== "", requireValueChange: true, };
    }
    else {
      caseNumberValidation = { required: true, validator: () => values.yTracCaseID > 0, requireValueChange: true, };
    }
    const role = values.roleSelectedItem?.contactRoleName;
    const namesAutoFilledByRole = role === "CP" || role === "NCP";

    return {
      caseNumberTxt: caseNumberValidation,
      noCaseChk: { required: values.caseNumberTxt === '' ? true : false },
      roleSelect: { required: true, validator: dropdownValidator },
      firstNameTxt: {required: true, validator: (value) => value?.trim() !== ""},
      lastNameTxt: {required: true, validator: (value) =>  value?.trim() !== ""},
      categorySelect: { required: true, validator: dropdownValidator },
      subCategorySelect: { required: true, validator: dropdownValidator},
      actionTakenSelect: { required: true, validator: dropdownValidator},
      iccSelect: {required: values.iccViewEnabled && values.iccEnabled, validator: dropdownValidator},
      noteForActionTxt: {
        required: values.tracNoteRequired,
        validator: (value) => !values.tracNoteRequired || (value?.trim() !== "")
      },
      narrativeForStateTxt: { required: false}
    };
};

  const getNoCaseValidationRules = (values) => {
    return {
      caseNumberTxt: { required: false },
      noCaseChk: { required: true, validator: (value) => value === true },
      roleSelect: { required: true, validator: dropdownValidator },
      firstNameTxt: { required: true, validator: (value) => value?.trim() !== "" },
      lastNameTxt: { required: true, validator: (value) => value?.trim() !== "" },
      categorySelect: { required: true, validator: dropdownValidator },
      subCategorySelect: { required: true, validator: dropdownValidator },
      actionTakenSelect: { required: true, validator: dropdownValidator },
      iccSelect: { required: values.iccViewEnabled && values.iccEnabled, validator: dropdownValidator },
      noteForActionTxt: {
        required: values.noCaseChk || values.tracNoteRequired,
        validator: (value) => !(values.noCaseChk || values.tracNoteRequired) || (value?.trim() !== "")
      },
      narrativeForStateTxt: { required: false }
    };
  };

 function getValidationRules(values, validationType) {
    switch (validationType) {
      case EnumUtility.CaseType.Case:
        return getCaseValidationRules(values);

      case EnumUtility.CaseType.NoCase:
        return getNoCaseValidationRules(values);

      default:
        break;
    }
  };

const clearCaseTabInfo = () => {
    if (caseInfoRef.current) {
      // Call the child method with parameters
      caseInfoRef.current.clearCaseInfoFromExternal(externalBatchID, showExtDocGrid);
    }
  };

  const formStateRef = useRef(formState);
  useEffect(() => { formStateRef.current = formState; }, [formState]);

 // Debounced validation using the centralized validateForm
  const debouncedValidation = useCallback(
    DebounceForm((values, validationType) => {
      try {
        // const rules = getValidationRules(values, validationType);

        // const validationResults = ValidateRequiredFieldsInForm(values, rules, formStateRef.current);

        // Use custom asterisk calculation for proper No Case handling
        const customAsterisks = calculateRequiredAsterisks(values, validationType);
        setRequiredFieldLabels(customAsterisks);
        // const isValid = validationResults.isFormValid;
      }
      catch (error) {
        throw LoggerUtility.ProcessErrorData(error, "debouncedValidation", {});
      }
    }, 300),
    []
  );

  useEffect(() => {
    if (
      pendingAutoAdd &&
      formValues.roleSelectedItem?.enableShortCircuit &&
      formValues.actionTakenSelectedItem?.resultGroupTypeID > 0 &&
      formValues.iccSelectedItem?.resultTypeID > 0
    ) {
      setPendingAutoAdd(false);
      proceedToAddToList(true);
    }
  }, [
    pendingAutoAdd,
    formValues.roleSelectedItem,
    formValues.actionTakenSelectedItem,
    formValues.iccSelectedItem,
  ]);
  
const isEmptyText = (v) =>
  v === null || v === undefined || (typeof v === "string" && v.trim() === "");

const isEmptyDropdown = (v) => {
  if (typeof v === "number") return v <= 0;
  if (!v) return true;
  return v.id == null || v.id <= 0;
};

const calculateRequiredAsterisks = (values, validationType, queuedActionsCount = resultsListOfActions.length) => {
  const rules = getValidationRules(values, validationType);

  const asterisks = {
    caseNumberTxt: "",
    noCaseChk: "",
    roleSelect: "",
    firstNameTxt: "",
    lastNameTxt: "",
    categorySelect: "",
    subCategorySelect: "",
    actionTakenSelect: "",
    iccSelect: "",
    noteForActionTxt: "",
    narrativeForStateTxt: "",
  };

  const caseOrNoCaseSelected = !isEmptyText(values.caseNumberTxt) || values.noCaseChk === true;

  const hasQueuedActions = queuedActionsCount > 0;

  if (rules.caseNumberTxt?.required && isEmptyText(values.caseNumberTxt)) {
    asterisks.caseNumberTxt = "*";
  }

  if (rules.noCaseChk?.required && values.noCaseChk !== true) {
    asterisks.noCaseChk = "*";
  }

  if (caseOrNoCaseSelected) {
    if (rules.roleSelect?.required && isEmptyDropdown(values.roleSelect)) {
      asterisks.roleSelect = "*";
    }

    if (!isEmptyDropdown(values.roleSelect)) {
      asterisks.roleSelect = "";

      asterisks.firstNameTxt =
        rules.firstNameTxt?.required && isEmptyText(values.firstNameTxt) ? "*" : "";

      asterisks.lastNameTxt =
        rules.lastNameTxt?.required && isEmptyText(values.lastNameTxt) ? "*" : "";

      asterisks.categorySelect =
        (!hasQueuedActions && rules.categorySelect?.required && isEmptyDropdown(values.categorySelect))
          ? "*"
          : "";

      asterisks.subCategorySelect =
        (!isEmptyDropdown(values.categorySelect) && rules.subCategorySelect?.required && isEmptyDropdown(values.subCategorySelect))
          ? "*"
          : "";

      asterisks.actionTakenSelect =
        (!isEmptyDropdown(values.categorySelect) && rules.actionTakenSelect?.required && isEmptyDropdown(values.actionTakenSelect))
          ? "*"
          : "";

      asterisks.iccSelect =
        rules.iccSelect?.required && isEmptyDropdown(values.iccSelect) ? "*" : "";

      asterisks.noteForActionTxt =
        rules.noteForActionTxt?.required && isEmptyText(values.noteForActionTxt) ? "*" : "";

      asterisks.narrativeForStateTxt =
        rules.narrativeForStateTxt?.required && isEmptyText(values.narrativeForStateTxt) ? "*" : "";
    }
  }

  return asterisks;
};

  useEffect(() => {
    // Initialize asterisks on component mount
    const updatedAsterisks = calculateRequiredAsterisks(formValues, caseValidationType);
    setRequiredFieldLabels(updatedAsterisks);
  }, [isCaseConfidentialNoPermission]);

  useEffect(() => {
    const parts = resultsListOfActions.map(item => item.NarrativeText).filter(t => t && t.trim() !== '');
    const raw = UtilityLibrary.Multipart_Narrative_Consolidator(parts);
    const consolidated = raw.replace(/^\s*\|\s*/, ""); // Remove leading pipe and whitespace
    narrativeForStateRef.current?.setValue(consolidated);
    setFormValues(prev => prev.narrativeForStateTxt === consolidated ? prev : { ...prev, narrativeForStateTxt: consolidated });
  }, [resultsListOfActions]);

  // Function to apply Form Fields Enable logic
function formFieldsEnableLogic(dispatch, logicType, config = {}) {

  switch (logicType) {
    case FormFieldEnableType.PageLoad.ID:
    case FormFieldEnableType.NewWalkInClick.ID:
      dispatch({ type: "SET_FIELD_STATE", payload: { field: "addToListBtn", isEnabled: false } });
      dispatch({ type: "SET_FIELD_STATE", payload: { field: "caseNumberTxt", isEnabled: true } });
      dispatch({ type: "SET_FIELD_STATE", payload: { field: "noCaseChk", isEnabled: true } });
      dispatch({ type: "SET_FIELD_STATE", payload: { field: "channelSelect", isEnabled: false } });
      dispatch({ type: "SET_FIELD_STATE", payload: { field: "roleSelect", isEnabled: false } });
      dispatch({ type: "SET_FIELD_STATE", payload: { field: "firstNameTxt", isEnabled: false } });
      dispatch({ type: "SET_FIELD_STATE", payload: { field: "lastNameTxt", isEnabled: false } });
      dispatch({ type: "SET_FIELD_STATE", payload: { field: "categorySelect", isEnabled: false } });
      dispatch({ type: "SET_FIELD_STATE", payload: { field: "subCategorySelect", isEnabled: false } });
      dispatch({ type: "SET_FIELD_STATE", payload: { field: "actionTakenSelect", isEnabled: false } });
      dispatch({ type: "SET_FIELD_STATE", payload: { field: "iccSelect", isEnabled: false } });
      dispatch({ type: "SET_FIELD_STATE", payload: { field: "noteForActionTxt", isEnabled: false } });
      dispatch({ type: "SET_FIELD_STATE", payload: { field: "narrativeForStateTxt", isEnabled: false } });
      dispatch({ type: "SET_FIELD_STATE", payload: { field: "saveBtn", isEnabled: false } });
      dispatch({ type: "SET_FIELD_STATE", payload: { field: "saveAnotherBtn", isEnabled: false } });
      dispatch({ type: "SET_FIELD_VISIBILITY", payload: { field: "iccSelect", isVisible: false } });
      break;
      
    case FormFieldEnableType.CaseNumberValid.ID:
      dispatch({ type: "SET_FIELD_STATE", payload: { field: "addToListBtn", isEnabled: false } });
      dispatch({ type: "SET_FIELD_STATE", payload: { field: "caseNumberTxt", isEnabled: true } });
      dispatch({ type: "SET_FIELD_STATE", payload: { field: "channelSelect", isEnabled: false } });
      dispatch({ type: "SET_FIELD_STATE", payload: { field: "roleSelect", isEnabled: true } });
      dispatch({ type: "SET_FIELD_STATE", payload: { field: "firstNameTxt", isEnabled: false } });
      dispatch({ type: "SET_FIELD_STATE", payload: { field: "lastNameTxt", isEnabled: false } });
      dispatch({ type: "SET_FIELD_STATE", payload: { field: "categorySelect", isEnabled: false } });
      dispatch({ type: "SET_FIELD_STATE", payload: { field: "subCategorySelect", isEnabled: false } });
      dispatch({ type: "SET_FIELD_STATE", payload: { field: "actionTakenSelect", isEnabled: false } });
      dispatch({ type: "SET_FIELD_STATE", payload: { field: "iccSelect", isEnabled: false } });
      dispatch({ type: "SET_FIELD_STATE", payload: { field: "noteForActionTxt", isEnabled: true } });
      dispatch({ type: "SET_FIELD_STATE", payload: { field: "narrativeForStateTxt", isEnabled: true } });
      dispatch({ type: "SET_FIELD_STATE", payload: { field: "saveBtn", isEnabled: false } });
      dispatch({ type: "SET_FIELD_STATE", payload: { field: "saveAnotherBtn", isEnabled: false } });

      dispatch({ type: "SET_FIELD_VISIBILITY", payload: { field: "iccSelect", isVisible: false } });
      break;

    case FormFieldEnableType.CaseNumberInValid.ID:
      dispatch({ type: "SET_FIELD_STATE", payload: { field: "addToListBtn", isEnabled: false } });
      dispatch({ type: "SET_FIELD_STATE", payload: { field: "caseNumberTxt", isEnabled: true } });
      dispatch({ type: "SET_FIELD_STATE", payload: { field: "channelSelect", isEnabled: false } });
      dispatch({ type: "SET_FIELD_STATE", payload: { field: "roleSelect", isEnabled: false } });
      dispatch({ type: "SET_FIELD_STATE", payload: { field: "firstNameTxt", isEnabled: false } });
      dispatch({ type: "SET_FIELD_STATE", payload: { field: "lastNameTxt", isEnabled: false } });
      dispatch({ type: "SET_FIELD_STATE", payload: { field: "categorySelect", isEnabled: false } });
      dispatch({ type: "SET_FIELD_STATE", payload: { field: "subCategorySelect", isEnabled: false } });
      dispatch({ type: "SET_FIELD_STATE", payload: { field: "actionTakenSelect", isEnabled: false } });
      dispatch({ type: "SET_FIELD_STATE", payload: { field: "iccSelect", isEnabled: false } });
      dispatch({ type: "SET_FIELD_STATE", payload: { field: "noteForActionTxt", isEnabled: false } });
      dispatch({ type: "SET_FIELD_STATE", payload: { field: "narrativeForStateTxt", isEnabled: false } });
      dispatch({ type: "SET_FIELD_STATE", payload: { field: "saveBtn", isEnabled: false } });
      dispatch({ type: "SET_FIELD_STATE", payload: { field: "saveAnotherBtn", isEnabled: false } });

      dispatch({ type: "SET_FIELD_VISIBILITY", payload: { field: "iccSelect", isVisible: false } });
      break;

      case FormFieldEnableType.NoCaseSelect.ID:
        dispatch({ type: "SET_FIELD_STATE", payload: { field: "addToListBtn", isEnabled: false } });
        dispatch({ type: "SET_FIELD_STATE", payload: { field: "caseNumberTxt", isEnabled: false } });
        dispatch({ type: "SET_FIELD_STATE", payload: { field: "channelSelect", isEnabled: false } });
        dispatch({ type: "SET_FIELD_STATE", payload: { field: "roleSelect", isEnabled: true } });
        dispatch({ type: "SET_FIELD_STATE", payload: { field: "firstNameTxt", isEnabled: true } });
        dispatch({ type: "SET_FIELD_STATE", payload: { field: "lastNameTxt", isEnabled: true } });
        dispatch({ type: "SET_FIELD_STATE", payload: { field: "categorySelect", isEnabled: true } });
        dispatch({ type: "SET_FIELD_STATE", payload: { field: "subCategorySelect", isEnabled: false } });
        dispatch({ type: "SET_FIELD_STATE", payload: { field: "actionTakenSelect", isEnabled: false } });
        dispatch({ type: "SET_FIELD_STATE", payload: { field: "iccSelect", isEnabled: false } });
        dispatch({ type: "SET_FIELD_STATE", payload: { field: "noteForActionTxt", isEnabled: true } });
        dispatch({ type: "SET_FIELD_STATE", payload: { field: "narrativeForStateTxt", isEnabled: true } });
        dispatch({ type: "SET_FIELD_STATE", payload: { field: "saveBtn", isEnabled: false } });
        dispatch({ type: "SET_FIELD_STATE", payload: { field: "saveAnotherBtn", isEnabled: false } });

        dispatch({ type: "SET_FIELD_VISIBILITY", payload: { field: "iccSelect", isVisible: false } });
        break;

    case FormFieldEnableType.RoleSelect.ID:
      dispatch({ type: "SET_FIELD_STATE", payload: { field: "addToListBtn", isEnabled: false } });
      dispatch({ type: "SET_FIELD_STATE", payload: { field: "roleSelect", isEnabled: true } });
       dispatch({ type: "SET_FIELD_STATE", payload: { field: "firstNameTxt", isEnabled: true } });
      dispatch({ type: "SET_FIELD_STATE", payload: { field: "lastNameTxt", isEnabled: true } });
      dispatch({ type: "SET_FIELD_STATE", payload: { field: "categorySelect", isEnabled: true } });
      dispatch({ type: "SET_FIELD_STATE", payload: { field: "subCategorySelect", isEnabled: false } });
      dispatch({ type: "SET_FIELD_STATE", payload: { field: "actionTakenSelect", isEnabled: false } });
      dispatch({ type: "SET_FIELD_STATE", payload: { field: "iccSelect", isEnabled: false } });
      dispatch({ type: "SET_FIELD_STATE", payload: { field: "noteForActionTxt", isEnabled: true } });
      dispatch({ type: "SET_FIELD_STATE", payload: { field: "narrativeForStateTxt", isEnabled: true } });
        dispatch({ type: "SET_FIELD_STATE", payload: { field: "saveBtn", isEnabled: false } });
      dispatch({ type: "SET_FIELD_STATE", payload: { field: "saveAnotherBtn", isEnabled: false } });
      break;

    case FormFieldEnableType.CategorySelect.ID:
      dispatch({ type: "SET_FIELD_STATE", payload: { field: "addToListBtn", isEnabled: false } });
      dispatch({ type: "SET_FIELD_STATE", payload: { field: "categorySelect", isEnabled: true } });
      dispatch({ type: "SET_FIELD_STATE", payload: { field: "subCategorySelect", isEnabled: true } });
      dispatch({ type: "SET_FIELD_STATE", payload: { field: "actionTakenSelect", isEnabled: true } });  // mjq was false
      dispatch({ type: "SET_FIELD_STATE", payload: { field: "iccSelect", isEnabled: false } });
      dispatch({ type: "SET_FIELD_STATE", payload: { field: "noteForActionTxt", isEnabled: true } });
      dispatch({ type: "SET_FIELD_STATE", payload: { field: "narrativeForStateTxt", isEnabled: true } });
        dispatch({ type: "SET_FIELD_STATE", payload: { field: "saveBtn", isEnabled: false } });
      dispatch({ type: "SET_FIELD_STATE", payload: { field: "saveAnotherBtn", isEnabled: false } });
      
      break;

    case FormFieldEnableType.SubCategorySelect.ID:
      dispatch({ type: "SET_FIELD_STATE", payload: { field: "addToListBtn", isEnabled: false } });
      dispatch({ type: "SET_FIELD_STATE", payload: { field: "subCategorySelect", isEnabled: true } });
      dispatch({ type: "SET_FIELD_STATE", payload: { field: "actionTakenSelect", isEnabled: true } });
      dispatch({ type: "SET_FIELD_STATE", payload: { field: "iccSelect", isEnabled: false } });
      dispatch({ type: "SET_FIELD_STATE", payload: { field: "noteForActionTxt", isEnabled: true } });
      dispatch({ type: "SET_FIELD_STATE", payload: { field: "narrativeForStateTxt", isEnabled: true } });
        dispatch({ type: "SET_FIELD_STATE", payload: { field: "saveBtn", isEnabled: false } });
      dispatch({ type: "SET_FIELD_STATE", payload: { field: "saveAnotherBtn", isEnabled: false } });
      
      break;

     case FormFieldEnableType.ActionTakenSelect.ID:
      dispatch({ type: "SET_FIELD_STATE", payload: { field: "addToListBtn", isEnabled: true } });
      dispatch({ type: "SET_FIELD_STATE", payload: { field: "actionTakenSelect", isEnabled: true } });
      dispatch({ type: "SET_FIELD_STATE", payload: { field: "iccSelect", isEnabled: true } });
      dispatch({ type: "SET_FIELD_STATE", payload: { field: "noteForActionTxt", isEnabled: true } });
      dispatch({ type: "SET_FIELD_STATE", payload: { field: "narrativeForStateTxt", isEnabled: true } });
        dispatch({ type: "SET_FIELD_STATE", payload: { field: "saveBtn", isEnabled: false } });
      dispatch({ type: "SET_FIELD_STATE", payload: { field: "saveAnotherBtn", isEnabled: false } });
      dispatch({ type: "SET_FIELD_VISIBILITY", payload: { field: "iccSelect", isVisible: true } });

      break;

    case FormFieldEnableType.IccSelect.ID:
      dispatch({ type: "SET_FIELD_STATE", payload: { field: "iccSelect", isEnabled: true } });
      dispatch({ type: "SET_FIELD_STATE", payload: { field: "noteForActionTxt", isEnabled: true } });
      dispatch({ type: "SET_FIELD_STATE", payload: { field: "narrativeForStateTxt", isEnabled: true } });
        dispatch({ type: "SET_FIELD_STATE", payload: { field: "saveBtn", isEnabled: false } });
      dispatch({ type: "SET_FIELD_STATE", payload: { field: "saveAnotherBtn", isEnabled: false } });
      dispatch({ type: "SET_FIELD_VISIBILITY", payload: { field: "iccSelect", isVisible: true } });

      break;

  }
};

  
  const handleNotificationDialog = (dialogContent, timerAutoClose = false, timeToClose = 5000) => {
      setNotificationDialogContent(dialogContent);
      setTimerNotificationDialogClose(timeToClose);
      setTimerAutoCloseNotificationDialog(timerAutoClose);
      handleOpenNotificationDialog();
  };

  const handleOpenNotificationDialog = () => {
    setNotificationDialogOpen(true);
  };

  const handleCloseNotificationDialog = useCallback(() => {
    setNotificationDialogOpen(false);
    
    // Set focus to Case Search field if a save operation just completed
    if (shouldFocusCaseSearch) {
      setShouldFocusCaseSearch(false);
      setTimeout(() => {
        if (caseNumberRef.current) {
          caseNumberRef.current.focus();
        }
      }, 100);
    }
  }, [shouldFocusCaseSearch]);

  const toUpperCase = (stringToUpperCase) => {
    return String(stringToUpperCase).toUpperCase();
  };


  const getWalkInFilteredRoles = (roles = [], excludeShortCircuit = false) => {
      return (roles ?? []).filter((role) => {
        const roleId = Number(role?.contactRoleID ?? -1);
        const roleDisplayOrder = Number(role?.displayOrder ?? -1);
        const isDroppedCall =
          roleId === ClientType.ContactRole.UNKN_Dropped_Call ||
          roleDisplayOrder === ClientType.ContactRole.UNKN_Dropped_Call;

        const isCallbackIncomplete =
          roleId === ClientType.ContactRole.UNKN_Callback_Incomplete ||
          roleDisplayOrder === ClientType.ContactRole.UNKN_Callback_Incomplete;

        const isShortCircuitRole = Boolean(role?.enableShortCircuit);

        return (
          !isDroppedCall &&
          !isCallbackIncomplete &&
          (!excludeShortCircuit || !isShortCircuitRole)
        );
      });

      // Sad but true; I used the same display order bug again in G4. Here is the original code from G2:

      // if (ClientEntities.ModuleID == (int)Enum_Master.Module.Workflow) // Only Workflow because of Walk-in
      // {
      //     // Build contact role list to not include Dropped Call [8807] or Callback Incomplete [40975]
      //     _ContactRoles = _ContactRoles.Where(cr => cr.DisplayOrder != (int)Enum_ClientType.ContactRole.UNKN_Dropped_Call && cr.DisplayOrder != (int)Enum_ClientType.ContactRole.UNKN_Callback_Incomplete).ToObservableCollection();
      //     // 2024/09/17 TAJ - this should really be refactored to add another column to ContactRole table for CC/WI filter
      //     // Why in the heck are we matching the DisplayOrder - that's messed up!  We obviously mismanaged the PK IDs across states and resorted to this hack.
      // }

    };

    const loadChannelRoleCategoryOptions = (channelRoleCategoriesData, excludeShortCircuit = false) => {
      const channels = channelRoleCategoriesData?.[0] ?? [];
      const roles = channelRoleCategoriesData?.[1] ?? [];
      const categories = channelRoleCategoriesData?.[2] ?? [];

      setChannelOptions(channels);
      setRoleOptions(getWalkInFilteredRoles(roles, excludeShortCircuit));
      setCategoryOptions(categories);

      const defaultChannel = channels?.find(opt => opt.channelTypeName === "Walk In") || null;
      setFormValues(prev => ({
        ...prev,
        channelSelect: defaultChannel?.channelTypeID,
        channelSelectedItem: defaultChannel
      }));
    };

  const loadChannelRoleCategoriesData= async() => {
    try {
      const channelRoleCategoriesData = await ApiManager.GetChannelRoleCategoryOptions({
        masterServerDBSchema,
        yTracSiteID: stateID
      });
      loadChannelRoleCategoryOptions(channelRoleCategoriesData)
  
      setChannelRoleCategoriesData(channelRoleCategoriesData);
  
      const resultListMapData = await ApiManager.GetResultListMapData({});
      setResultListMapData(resultListMapData);
    } catch (err) {
      console.error("Error fetching results:", err);
    }
};

  // Function to handle New WalkIn Click
  const handleNewWalkInClick = async () => {
    setIsPageLoading(true);
    try {
      await simulateAsyncDelay();
      setIsEditable(true);
      setAreDropdownsLocked(false);   // ← add this
      formFieldsEnableLogic(dispatch, FormFieldEnableType.NewWalkInClick.ID);
      loadChannelRoleCategoryOptions(channelRoleCategoriesData)
    } catch (error) {
      await LoggerUtility.ManageErrorLogging(
        error,
        errorFormName,
        "handleNewWalkInClick",
      );
    } finally {
      setIsPageLoading(false);
    }
  };

   const changeCaseNumberIconsDisable = (iconsEnabledValue) => {// RC removed async as its not needed
      try {
        const updatedCaseNumberIconsEnabled = {
          ...caseNumberIconsDisabled,
          search: iconsEnabledValue.search,
          copy: iconsEnabledValue.copy,
          clear: iconsEnabledValue.clear,
        };
        setCaseNumberIconsDisabled((prevValues) => ({
          ...prevValues,
          ...updatedCaseNumberIconsEnabled
        }));
      }
      catch (error) {
        throw LoggerUtility.ProcessErrorData(error, "changeCaseNumberIconsDisable", {});
      }
    };

   const checkCaseNumberRegEx = (caseNumber) => { // RC removed async as its not needed
      try {
        let isValidFormat = true;
        const caseNumberRegexValue = new RegExp(caseNumberRegex.caseRegexValue || "");
  
        if ((caseNumberRegexValue !== "") && (caseNumber !== "")) {
          if (!caseNumberRegexValue.test(caseNumber)) {
            caseNumberRef.current.setValidationMessage(caseNumberRegex.caseRegexMessage);
            changeCaseNumberIconsDisable({ search: true, copy: false, clear: false });
            isValidFormat = false;
          }
          else {
            caseNumberRef.current.setValidationMessage("");
            changeCaseNumberIconsDisable({ search: false, copy: false, clear: false });
          }
        }
        else {
          caseNumberRef.current.setValidationMessage("");
          changeCaseNumberIconsDisable({ search: false, copy: false, clear: false });
        }
  
        return isValidFormat;
      }
      catch (error) {
        throw LoggerUtility.ProcessErrorData(error, "checkCaseNumberRegEx", {});
      }
  };

useEffect(() => {
  const fetchResults = async () => {
    try {
     await loadChannelRoleCategoriesData()
    } catch (err) {
      console.error("Error fetching results:", err);
    }
  };

  fetchResults();
  }, []);


  const handleCaseSearch = async () => {
    try {
      await simulateAsyncDelay();
      const isValidCaseFormat = await checkCaseNumberRegEx(formValues.caseNumberTxt);
      if (!isValidCaseFormat) {//TODO check requirements doc
        clearFormFields(FormActionType.Case.ID);
        // handleNotificationDialog(EnumUtility.BatchDocumentProcess.CaseNumberAddedAgain);//TODO
      }
      else {
        setIsPageLoading(true);
        const caseID = toUpperCase(formValues.caseNumberTxt);
        if (caseInfoRef.current) {
          await caseInfoRef.current.onSelectCaseFromExternal(caseID, false, 100);
        }
      }
    }
    catch (error) {
      await LoggerUtility.ManageErrorLogging(error, errorFormName, "handleCaseSearch");
    }
    finally { setIsPageLoading(false); }
  };

  // Function to handle clear of case search value
  const handleCaseClear = async () => {
    setIsPageLoading(true);
    try {
      await simulateAsyncDelay();
      setIsCaseConfidentialNoPermission(false); // reset confidentiality flag
      clearFormFields(FormActionType.Case.ID)
      clearCaseTabInfo();
      caseNumberRef.current?.setValidationMessage("");
      // Apply proper field enable logic to disable workflow and related fields
      formFieldsEnableLogic(dispatch, FormFieldEnableType.NewWalkInClick.ID);
    }
    catch (error) {
      await LoggerUtility.ManageErrorLogging(error, errorFormName, "handleCaseClear");
    }
    finally { setIsPageLoading(false); }
  };

  // Helper function to update form values
  function updateArrayStateValues(arrayStateSetter, updatedValues) {
    arrayStateSetter((prevValues) => ({
      ...prevValues,
      ...updatedValues
    }));
  };

    // Function to handle case search completion
    const handleCaseSearchComplete = async (hasValidCase, caseSearchInfo, caseSearchSource) => {
      try {
        setIsPageLoading(false);
        let updatedFormValues = formValues;
        
        const caseInfo = caseSearchInfo.caseData;
        setcaseSearchMFData(caseInfo);
        if (hasValidCase) {
          const loginUserInfo = sessionService.getUser();
          let dialogContent;
          timerRef.current.startTimer();
          setWalkinStartTime(UtilityLibrary.getCurrentDateTimeForTimeZone(timeZone));

      if (caseSearchInfo?.isCaseConfidential) {
        if (caseSearchInfo?.isConfidentialForThisUser) {
          setIsCaseConfidentialNoPermission(true);
        } else {
          setIsCaseConfidentialNoPermission(false);
        }
      } else {
        setIsCaseConfidentialNoPermission(false);
      }

      const ContactNextIDApiUrl = await ApiEndpoints.ContactNextIDApiUrl();
      const contactID = await ApiManager.GetDataWithParamsFromAPI(
        ContactNextIDApiUrl,
        { siteID, masterServerDBSchema },
        {headers: { "Cache-Control": "no-cache", Pragma: "no-cache" }},
        101
      );

      const selectedRole = formValues?.roleSelectedItem;

      updatedFormValues = {
        ...formValues,
        yTracCaseID: caseInfo.yTracCaseID,
        caseNumberTxt: caseInfo.caseNumber,
        caseNumberSearched: caseInfo.caseNumber,
        contactID,
        cpFirstName: caseInfo.cpFirstName,
        cpLastName: caseInfo.cpLastName,
        ncpFirstName: caseInfo.ncpFirstName,
        ncpLastName: caseInfo.ncpLastName,
      };

      if (
        selectedRole?.contactRoleID === ClientType.ContactRole.CP ||
        selectedRole?.contactRoleID === ClientType.ContactRole.NCP
      ) {
        updatedFormValues = {
          ...updatedFormValues,
          firstNameTxt:
            selectedRole.contactRoleID === ClientType.ContactRole.NCP
              ? caseInfo.ncpFirstName
              : caseInfo.cpFirstName,
          lastNameTxt:
            selectedRole.contactRoleID === ClientType.ContactRole.NCP
              ? caseInfo.ncpLastName
              : caseInfo.cpLastName,
          contactNamesEnabled: false,
        };
      }

      formFieldsEnableLogic(dispatch, FormFieldEnableType.CaseNumberValid.ID);
      changeCaseNumberIconsDisable({ search: false, copy: false, clear: false });

      loadChannelRoleCategoryOptions(channelRoleCategoriesData, true);
      updateArrayStateValues(setFormValues, updatedFormValues);

      const customAsterisks = calculateRequiredAsterisks(updatedFormValues, caseValidationType);
      setRequiredFieldLabels(customAsterisks);
    } else {
      if (siteSettingDetail.CaseNumberInsertAllow === "1") {
        updatedFormValues = {
          ...formValues,
          yTracCaseID: 0,
          caseNumberSearched: formValues.caseNumberTxt,
        };

        formFieldsEnableLogic(dispatch, FormFieldEnableType.CaseNumberValid.ID);
        changeCaseNumberIconsDisable({ search: false, copy: false, clear: false });

        updateArrayStateValues(setFormValues, updatedFormValues);
      } else {
        timerRef.current.resetTimer();
        setWalkinStartTime(null);
        let shouldPreserve = false;
        let errorMessage = {};

        if (caseSearchInfo.statusCode === EnumUtility.CaseSearchResultCode.CaseNotFound.ID) {
          const loginUserInfo = sessionService.getUser();
          const userStateID = loginUserInfo ? loginUserInfo.stateID : -1;
          errorMessage = EnumUtility.getCaseNumberNotFoundError(userStateID);
          shouldPreserve = true;

          if (!errorMessage) {
            return;
          }
        } else if (
          [
            EnumUtility.CaseSearchResultCode.MFSessionDisconnectError.ID,
            EnumUtility.CaseSearchResultCode.ScraperDisconnectedError.ID
          ].includes(caseSearchInfo.statusCode)
        ) {
          let messageContentID = null;

          if (
            caseSearchInfo.statusCode ===
            EnumUtility.CaseSearchResultCode.MFSessionDisconnectError.ID
          ) {
            messageContentID =
              ClientType.MessageContent.E_MF7049_Lost_Connection_To_State_System;
          } else {
            messageContentID =
              ClientType.MessageContent.E_UI7056_Scrapper_Connection_Status;
          }

          const messageContent = await SharedService.getMessageContent(messageContentID);
          errorMessage = {
            NotificationType: "error",
            Message: messageContent?.messageText?.replace(/\n/g, "\u00A0\n") ?? ""
          };
          shouldPreserve = true;
        } else if (
          caseSearchInfo.statusCode ===
          EnumUtility.CaseSearchResultCode.Enter_A_Function_Error.ID
        ) {
          const messageContent = await SharedService.getMessageContent(
            ClientType.MessageContent.N_UI7058_ENTER_A_FUNCTION_Error_Occured
          );
          errorMessage = {
            NotificationType: "error",
            Message: messageContent?.messageText?.replace(/\n/g, "\u00A0\n") ?? ""
          };
          shouldPreserve = true;
        } else {
          errorMessage = {
            ID: 321,
            Name: "NetworkError",
            NotificationType: EnumUtility.NotificationType.Warning,
            Title: "Warning",
            Message: caseSearchInfo.errorMessage
          };
        }

        if (FormFieldEnableType.BatchSelect) {
          formFieldsEnableLogic(dispatch, FormFieldEnableType.BatchSelect.ID);
        }

        const dialogContent = {
          ...errorMessage,
          dynamicMessageContent: [formValues.caseNumberTxt],
        };
      }
    }
  } catch (error) {
    await LoggerUtility.ManageErrorLogging(error, errorFormName, "handleCaseSearchComplete");
  }
};
  //TODO create roleOptionsList state
  
const handleNoCaseChange = async (e) => {
  try {
    const { name, checked } = e.target;
    const validationType = checked
      ? EnumUtility.CaseType.NoCase
      : EnumUtility.CaseType.Case;

    setIsCaseConfidentialNoPermission(false);
    setRoleOptions(getWalkInFilteredRoles(channelRoleCategoriesData?.[1]));

    if (checked) {
      timerRef.current.startTimer();
      setWalkinStartTime(UtilityLibrary.getCurrentDateTimeForTimeZone(timeZone));
      formFieldsEnableLogic(dispatch, FormFieldEnableType.NoCaseSelect.ID);
      setCaseValidationType(validationType);
      caseNumberRef.current?.setValidationMessage("");
      setcaseSearchMFData({});
      clearFormFields(FormActionType.NoCaseChck.ID);
      clearCaseTabInfo();

      const ContactNextIDApiUrl = await ApiEndpoints.ContactNextIDApiUrl();
      const contactID = await ApiManager.GetDataWithParamsFromAPI(
        ContactNextIDApiUrl,
        { siteID, masterServerDBSchema },
        {headers: { "Cache-Control": "no-cache", Pragma: "no-cache" }},
        101
      );

      const updatedFormValues = {
        ...formValues,
        caseNumberTxt: "",
        yTracCaseID: 0,
        caseNumberSearched: "",
        noCaseChk: true,
        isNoCase: 1,
        tracNoteRequired: true,
        contactID,
        contactNamesEnabled: true,
        firstNameTxt: formValues.firstNameTxt || "",
        lastNameTxt: formValues.lastNameTxt || "",
      };

      updateArrayStateValues(setFormValues, updatedFormValues);
      loadChannelRoleCategoryOptions(channelRoleCategoriesData);
    } else {
      timerRef.current.resetTimer();
      setWalkinStartTime(null);
      formFieldsEnableLogic(dispatch, FormFieldEnableType.CaseNumberInValid.ID);
      validationType = EnumUtility.CaseType.Case;
      setCaseValidationType(validationType);
      clearNewWorkflowForm();
      clearCaseTabInfo();

      const updatedFormValues = {
        ...formValues,
        noCaseChk: false,
        isNoCase: 0,
        tracNoteRequired: false,
        contactNamesEnabled: false,
      };

      updateArrayStateValues(setFormValues, updatedFormValues);
    }

    const finalFormValues = {
      ...formValues,
      [name]: checked,
      isNoCase: checked ? 1 : 0,
      tracNoteRequired: checked ? true : false,
      contactNamesEnabled: checked ? true : false,
      firstNameTxt: formValues.firstNameTxt,
      lastNameTxt: formValues.lastNameTxt,
      companyNameTxt: formValues.companyNameTxt,
    };

    if (checked) {
      finalFormValues.caseNumberTxt = "";
      finalFormValues.yTracCaseID = 0;
      finalFormValues.caseNumberSearched = "";
      finalFormValues.noCaseChk = true;
      finalFormValues.isNoCase = 1;
      finalFormValues.contactNamesEnabled = true;
    } else {
      finalFormValues.noCaseChk = false;
      finalFormValues.isNoCase = 0;
      finalFormValues.contactNamesEnabled = false;
    }

    setRequiredFieldLabels(calculateRequiredAsterisks(finalFormValues, validationType));
    debouncedValidation(finalFormValues, validationType);

    setAreDropdownsLocked(false);

    if (noteForActionRef.current) {
      noteForActionRef.current.clear();
    }
    if (narrativeForStateRef.current) {
      narrativeForStateRef.current.clear();
    }
  } catch (error) {
    await LoggerUtility.ManageErrorLogging(error, errorFormName, "handleNoCaseChange");
  }
};

const handleCaseChange = async (e) => {
  try {
    // RC do not make any async calls before case number 
    // state update to ensure the case number cursor stays 
    // in place while editing
    const valueText = e.target.value;
    checkCaseNumberRegEx(valueText);
    // setEnableCaseSearch(isValidCaseFormat)
    // Update input value
     const updatedFormValues = { 
        ...formValues, 
        [ e.target.name]: valueText,
        roleSelect: initObjects.role,
        categorySelect: initObjects.category,
        subCategorySelect: initObjects.subCategory,
        actionTakenSelect: initObjects.actionTaken,
        iccSelect: initObjects.icc,
        iccViewEnabled: false,
      };
      setFormValues(updatedFormValues);
  }
  catch (error) {
    await LoggerUtility.ManageErrorLogging(error, errorFormName, "handleCaseChange");
  }
};
  
  const handleChannelChange = async (e, newValue) => {//TODO check if more is required
    try{
            const updatedFormValues = {
            ...formValues,
            channelSelect: newValue.channelTypeID,
            channelSelectedItem: newValue,
        }
      UtilityLibrary.updateArrayStateValues(setFormValues, updatedFormValues);
      setRoleOptions(getWalkInFilteredRoles(channelRoleCategoriesData?.[1]));
    }
    catch (error) {
      await LoggerUtility.ManageErrorLogging(error, errorFormName,"handleChannelChange");
    }
  }

  const loadContactName = (updatedValues) => {
    let updatedFormValues = {
        ...updatedValues,
        firstNameTxt: "",       // ← add these two lines
        lastNameTxt: "",        // ← add these two lines
        contactNamesEnabled: true,
      };

    //if ((SelectedContactChannel?.ChannelTypeID == (int)Enum_ClientType.ChannelType.Walk_In) && ((SelectedContactRole?.ContactRoleID == (int)Enum_ClientType.ContactRole.CP) || (SelectedContactRole?.ContactRoleID == (int)Enum_ClientType.ContactRole.NCP)))
    if (updatedValues?.roleSelectedItem?.contactRoleID === ClientType.ContactRole.NCP) {
      updatedFormValues = {
        ...updatedValues,
        firstNameTxt: caseSearchMFData?.ncpFirstName,
        lastNameTxt: caseSearchMFData?.ncpLastName,
        contactNamesEnabled: false,
      };
    }

    if (updatedValues?.roleSelectedItem?.contactRoleID === ClientType.ContactRole.CP) {
      updatedFormValues = {
        ...updatedValues,
        firstNameTxt: caseSearchMFData?.cpFirstName,
        lastNameTxt: caseSearchMFData?.cpLastName,
        contactNamesEnabled: false,
      };
    }
    return updatedFormValues;
};

const handleRoleChange = async (e, newValue, reason) => {
    try {
      //const { name, value } = e.target;
      let selectedValue = "";
      let roleSelectedItemValue = initRoleSelectedItem;

      setPendingAutoAdd(false);

      if (reason !== "clear" && newValue) {
        selectedValue = newValue.contactRoleID;
        roleSelectedItemValue = newValue;
      }

      if (reason === "clear" || selectedValue === "") {

        const updatedFormValues = {
          ...formValues,
          firstNameTxt: "",
          lastNameTxt: "",
          roleSelect: initObjects.role,
          roleSelectedItem: initRoleSelectedItem,
          categorySelect: initObjects.category,
          categorySelectedItem: initCategorySelectedItem,
          subCategorySelect: initObjects.subCategory,
          subCategorySelectedItem: initSubCategorySelectedItem,
          actionTakenSelect: initObjects.actionTaken,
          actionTakenSelectedItem: initActionTakenSelectedItem,
          iccSelect: initObjects.icc,
          iccViewEnabled: false,
          iccSelectedItem: initIccSelectedItem,
        }
        // reset subcategory options because they are dependent on category select
        setSubCategoryOptions([]);

        UtilityLibrary.updateArrayStateValues(setFormValues, updatedFormValues);

        formFieldsEnableLogic(dispatch, FormFieldEnableType.CaseNumberValid.ID);

        const requiredAsterisks = calculateRequiredAsterisks(updatedFormValues, caseValidationType);
        setRequiredFieldLabels(requiredAsterisks);
        return;
      }
      
      formFieldsEnableLogic(dispatch, FormFieldEnableType.RoleSelect.ID);

      let updatedFormValues = {
        ...formValues,
        roleSelect: selectedValue,
        roleSelectedItem: roleSelectedItemValue,
        categorySelect: initObjects.category,
        categorySelectedItem: initCategorySelectedItem,
        subCategorySelect: initObjects.subCategory,
        subCategorySelectedItem: initSubCategorySelectedItem,
        actionTakenSelect: initObjects.actionTaken,
        actionTakenSelectedItem: initActionTakenSelectedItem,
        iccSelect: initObjects.icc,
        iccSelectedItem:initIccSelectedItem,
        iccViewEnabled: false,
        iccEnabled: false
      };

      // const UNKN_ContactRolesWithCase =
      //   ClientType.SettingsDetail.UI_Miscellaneous__Include_UNKN_ContactRoles_For_With_Case;

      // const params = {
      //   settingsDetailIDList: String(UNKN_ContactRolesWithCase),
      //   yTracSiteID: siteID,
      // };

      // const results = await ApiManager.GetSettingsDetailValuesFromApi(params);

      if (roleSelectedItemValue.enableShortCircuit) {
        // SHORT-CIRCUIT: auto-populate and lock
        await handleShortCircuitRole(roleSelectedItemValue, updatedFormValues);
        return;
      } else {
        // NON-SHORT-CIRCUIT: normal name loading + category filtering
        setPendingAutoAdd(false);
        if (Object.keys(caseSearchMFData).length > 0) {
          updatedFormValues = loadContactName(updatedFormValues);
        }
        const filteredCategories = getFilteredCategoryOptions();
        setCategoryOptions(filteredCategories);

        setFormValues(updatedFormValues);
        const requiredAsterisks = calculateRequiredAsterisks(updatedFormValues, caseValidationType);
        setRequiredFieldLabels(requiredAsterisks);
      }

    }
    catch (error) {
      await LoggerUtility.ManageErrorLogging(error, errorFormName, "handleRoleChange");
    }
  };

const getFilteredCategoryOptions = () => {
  const shortCircuitCategoryIDs = new Set(
    roleOptions
      .filter(r => r.enableShortCircuit)
      .map(r => r.categoryTypeID)
      .filter(Boolean)
  );

  return channelRoleCategoriesData?.[2]  // use the unfiltered source
    ?.filter(c => !shortCircuitCategoryIDs.has(c.categoryTypeID))
    ?.map(c => ({
      ...c,
      subCategoryTypes: (c.subCategoryTypes ?? []).filter(sc =>
        sc.categorySubCategoryFilterID === ClientType.DropDownType.Category_Sub_Category_Filter__Contact_Center_And_WalkIn ||
        sc.categorySubCategoryFilterID === ClientType.DropDownType.Category_Sub_Category_Filter__WalkIn_Only
      )
    }))
    ?.filter(c => (c.subCategoryTypes?.length ?? 0) > 0) ?? [];
};

const handleShortCircuitRole = async (roleItem, baseFormValues) => {
  try {
    const bFullShortCircuit = roleItem.subCategoryTypeID != null && roleItem.subCategoryTypeID !== 0;

    const [firstNameContent, lastNameContent, tracNoteContent] = await Promise.all([
      SharedService.getMessageContent(roleItem.fkM_FirstName_MessageContentID),
      SharedService.getMessageContent(roleItem.fkM_LastName_MessageContentID),
      SharedService.getMessageContent(roleItem.fkM_TracNote_MessageContentID),
    ]);

    const firstName = firstNameContent?.messageText ?? "";
    const lastName  = lastNameContent?.messageText ?? "";
    const tracNote  = tracNoteContent?.messageText ?? "";

    // Restore full unfiltered categories for short-circuit (short-circuit category was excluded by filtering)
    const allCategories = channelRoleCategoriesData?.[2] ?? [];
    setCategoryOptions(allCategories);
    const selectedCategory = allCategories.find(c => c.categoryTypeID === roleItem.categoryTypeID);

    let scFormValues = {
      ...baseFormValues,
      firstNameTxt: firstName,
      lastNameTxt: lastName,
      contactNamesEnabled: false,
      categorySelect: selectedCategory?.categoryTypeID ?? initObjects.category,
      categorySelectedItem: selectedCategory ?? initCategorySelectedItem,
      subCategorySelect: initObjects.subCategory,
      subCategorySelectedItem: initSubCategorySelectedItem,
      actionTakenSelect: initObjects.actionTaken,
      actionTakenSelectedItem: initActionTakenSelectedItem,
      iccSelect: initObjects.icc,
      iccSelectedItem: initIccSelectedItem,
      iccViewEnabled: false,
    };

    if (selectedCategory?.subCategoryTypes) {
      setSubCategoryOptions(selectedCategory.subCategoryTypes);
    }

    noteForActionRef.current?.setValue(tracNote);
    // also sync formValues.noteForActionTxt
    scFormValues = { ...scFormValues, noteForActionTxt: tracNote };

    if (bFullShortCircuit) {
      const selectedSubCat = selectedCategory?.subCategoryTypes?.find(
        sc => sc.subCategoryTypeID === roleItem.subCategoryTypeID
      );

      const resultListTypeID = resultListMapData.find(rlm =>
        (rlm.channelTypeID === baseFormValues.channelSelectedItem?.channelTypeID || rlm.channelTypeID == null) &&
        (rlm.categoryTypeID === selectedCategory?.categoryTypeID || rlm.categoryTypeID == null) &&
        (rlm.subCategoryTypeID === selectedSubCat?.subCategoryTypeID || rlm.subCategoryTypeID == null)
      )?.resultListTypeID;

      const resultListTypesData = await ApiManager.GetResultListTypeData({ resultListTypeID });
      const allResultTypes = resultListTypesData?.[0]?.[0]?.resultTypes ?? [];
      setResultTypes(allResultTypes);

      const targetResultType = allResultTypes.find(rt => rt.resultTypeID === roleItem.resultTypeID);
      const targetGroupTypeID = targetResultType?.resultGroupTypeID;

      const allGroupTypes = allResultTypes
        .flatMap(rt => rt.resultGroupTypes || [])
        .filter((item, idx, self) => idx === self.findIndex(t => t.resultGroupTypeID === item.resultGroupTypeID));
      setActionTakenOptions(allGroupTypes);

      const targetGroupType = allGroupTypes.find(rgt => rgt.resultGroupTypeID === targetGroupTypeID);
      const iccChoices = allResultTypes.filter(rt => rt.resultGroupTypeID === targetGroupTypeID);
      setIccOptions(iccChoices);

      scFormValues = {
        ...scFormValues,
        subCategorySelect: selectedSubCat?.subCategoryTypeID ?? initObjects.subCategory,
        subCategorySelectedItem: selectedSubCat ?? initSubCategorySelectedItem,
        actionTakenSelect: targetGroupTypeID,
        actionTakenSelectedItem: targetGroupType ?? initActionTakenSelectedItem,
        iccSelect: roleItem.resultTypeID,
        iccSelectedItem: targetResultType ?? initIccSelectedItem,
        iccViewEnabled: false,
        iccEnabled: false,
      };

      setFormValues(scFormValues);
      setAreDropdownsLocked(true);
      setPendingAutoAdd(true); // ← triggers auto-queue via useEffect (see below)
    } else {
      // Partial short-circuit: category pre-selected, subcategory manual
      setFormValues(scFormValues);
      formFieldsEnableLogic(dispatch, FormFieldEnableType.CategorySelect.ID);
    }

    const requiredAsterisks = calculateRequiredAsterisks(scFormValues, caseValidationType);
    setRequiredFieldLabels(requiredAsterisks);
  } catch (error) {
    await LoggerUtility.ManageErrorLogging(error, errorFormName, "handleShortCircuitRole");
  }
};

const handleNameChange = async (event) => {
  try {
    const { name, value } = event.target;

    const updatedFormValues = {
      ...formValues,
      [name]: value,
    };

    setFormValues(updatedFormValues);

      const isFirstNameFilled = updatedFormValues.firstNameTxt?.trim() !== "";

      const isLastNameFilled = updatedFormValues.lastNameTxt?.trim() !== "";

        if (isFirstNameFilled && isLastNameFilled) {
            formFieldsEnableLogic(dispatch, FormFieldEnableType.CategorySelect.ID);
        } 
        else {
            formFieldsEnableLogic(dispatch, FormFieldEnableType.RoleSelect.ID);
        }

      debouncedValidation(updatedFormValues, caseValidationType);

      return updatedFormValues;
  } catch (error) {
    await LoggerUtility.ManageErrorLogging(error, errorFormName, "handleNameChange");
  }
};

const handleCompanyNameChange = async (event) => {
  try {
    const { name, value } = event.target;

    const updatedFormValues = {
      ...formValues,
      [name]: value,
    };

    setFormValues(updatedFormValues);

    return updatedFormValues;
  } catch (error) {
    await LoggerUtility.ManageErrorLogging(error, errorFormName, "handleCompanyNameChange");
  }
};

const handleCategoryChange = async (e, newValue, reason) => {
  try {
     //const { name, value } = e.target;
      let selectedValue = "";
      let categorySelectedItemValue = initCategorySelectedItem;

     if (reason !== "clear" && newValue) {
        selectedValue = newValue.categoryTypeID;
        categorySelectedItemValue = newValue;
      }
    
    if (reason === "clear" || !newValue) {

      const updatedFormValues = {
        ...formValues,
          categorySelect: initObjects.category,
          categorySelectedItem: initCategorySelectedItem,
          subCategorySelect: initObjects.subCategory,
          subCategorySelectedItem: initSubCategorySelectedItem,
          actionTakenSelect: initObjects.actionTaken,
          actionTakenSelectedItem: initActionTakenSelectedItem,
          iccSelect: initObjects.icc,
          iccViewEnabled: false,
          iccSelectedItem: initIccSelectedItem,
      };

      setSubCategoryOptions([]);
      setActionTakenOptions([]);
      setIccOptions([]);

      UtilityLibrary.updateArrayStateValues(setFormValues, updatedFormValues);

      formFieldsEnableLogic(dispatch, FormFieldEnableType.RoleSelect.ID);

      const requiredAsterisks = calculateRequiredAsterisks(updatedFormValues, caseValidationType);
      setRequiredFieldLabels(requiredAsterisks);

      return;
    }

    formFieldsEnableLogic(dispatch, FormFieldEnableType.CategorySelect.ID);

     const updatedFormValues = {
        ...formValues,
        categorySelect: selectedValue,
        categorySelectedItem: categorySelectedItemValue,
        subCategorySelect: (newValue?.subCategoryTypes?.length === 1) ? newValue.subCategoryTypes[0].subCategoryTypeID: initObjects.subCategory,
        subCategorySelectedItem:   (newValue?.subCategoryTypes?.length === 1) ? newValue.subCategoryTypes[0] : initSubCategorySelectedItem,
        actionTakenSelect: initObjects.actionTaken,
        actionTakenSelectedItem: initActionTakenSelectedItem,
        iccSelect: initObjects.icc,
        iccSelectedItem: initIccSelectedItem,
        iccViewEnabled: false,
      }
    setFormValues(updatedFormValues);
    // Update sub-category options based on selected category
    setSubCategoryOptions(newValue?.subCategoryTypes);
    
    const requiredAsterisks = calculateRequiredAsterisks(updatedFormValues, caseValidationType);
    setRequiredFieldLabels(requiredAsterisks);

  } catch (error) {
    await LoggerUtility.ManageErrorLogging(error, errorFormName,"handleCategoryChange");
  }
};


const handleSubCategoryChange = async (e, newValue, reason) => {
  try {
    let selectedValue = "";
    let subCategorySelectedItemValue = initSubCategorySelectedItem;

    if (reason !== "clear" && newValue) {
        selectedValue = newValue.subCategoryTypeID;
        subCategorySelectedItemValue = newValue;
    }
    if (reason === "clear" || selectedValue === "") {

      const updatedFormValues = {
        ...formValues,
        subCategorySelectedItem: initSubCategorySelectedItem,
        subCategorySelect: initObjects.subCategory,
        actionTakenSelect: initObjects.actionTaken,
        actionTakenSelectedItem: initActionTakenSelectedItem,
        iccSelect: initObjects.icc,
        iccViewEnabled: false,
        iccSelectedItem: initIccSelectedItem,
      };

      setActionTakenOptions([]);
      setIccOptions([]);

      UtilityLibrary.updateArrayStateValues(setFormValues, updatedFormValues);

      formFieldsEnableLogic(dispatch, FormFieldEnableType.CategorySelect.ID);

      const requiredAsterisks = calculateRequiredAsterisks(updatedFormValues, caseValidationType);
      setRequiredFieldLabels(requiredAsterisks);
      debouncedValidation(updatedFormValues, caseValidationType);

      return;
    }

    formFieldsEnableLogic(dispatch, FormFieldEnableType.SubCategorySelect.ID);

     const updatedFormValues = {
        ...formValues,
        subCategorySelect: selectedValue,
        subCategorySelectedItem: subCategorySelectedItemValue,
        actionTakenSelect: initObjects.actionTaken,
        actionTakenSelectedItem: initActionTakenSelectedItem,
        iccSelect: initObjects.icc,
        iccSelectedItem: initIccSelectedItem,
        iccViewEnabled: false,
    }
    setFormValues(updatedFormValues);
    const requiredAsterisks = calculateRequiredAsterisks(updatedFormValues, caseValidationType);
    setRequiredFieldLabels(requiredAsterisks);

  } catch (error) {
    await LoggerUtility.ManageErrorLogging(error, errorFormName,"handleSubCategoryChange");
  }
};

const getICCOptions = async(actionTakenSelectedItem) => {
   const CatSubDisplayOrder = ClientType.SettingsDetail.UI_Miscellaneous__CatSubCatResult_Display_Order_Sort;
    const params = {
      settingsDetailIDList: String(CatSubDisplayOrder),
      yTracSiteID: siteID
    }
    const results = await ApiManager.GetSettingsDetailValuesFromApi(params);
    let displayorder = results?.settingDetailsApiData?.[0]?.settingsDetailValue;

    let icc = resultTypes.filter(s => s.resultGroupTypeID === actionTakenSelectedItem?.resultGroupTypeID);
    const InputChannelChoices = displayorder === "0"
      ? [...icc].sort((a, b) => a.resultTypeShortName.localeCompare(b.resultTypeShortName))
              : [...icc].sort((a, b) => a.displayOrder - b.displayOrder);
    
    let iccViewEnabled = true, iccEnabled = true;
    if (InputChannelChoices.length === 1){
      iccViewEnabled = false
      iccEnabled = false;
    }
    return {iccOptions: InputChannelChoices, iccViewEnabled, iccEnabled};
}
  

const handleActionTakenChange = async (e, newValue, reason) => {
  try {

    let selectedValue = "";
    let actionTakenSelectedItemValue = initActionTakenSelectedItem;

    if (reason !== "clear" && newValue) {
        selectedValue = newValue.resultGroupTypeID;
        actionTakenSelectedItemValue = newValue;
    }

    if (reason === "clear" || selectedValue === "") {

      const updatedFormValues = {
        ...formValues,
        actionTakenSelect: initObjects.actionTaken,
        actionTakenSelectedItem: initActionTakenSelectedItem,
        iccSelect: initObjects.icc,
        iccViewEnabled: false,
        iccSelectedItem: initIccSelectedItem,
      };

      setIccOptions([]);

      UtilityLibrary.updateArrayStateValues(setFormValues, updatedFormValues);

      formFieldsEnableLogic(dispatch, FormFieldEnableType.SubCategorySelect.ID);

      setRequiredFieldLabels(
        calculateRequiredAsterisks(updatedFormValues, caseValidationType)
      );
      return;
    }

    formFieldsEnableLogic(dispatch, FormFieldEnableType.ActionTakenSelect.ID);

    const {iccOptions, iccViewEnabled, iccEnabled} = await getICCOptions(actionTakenSelectedItemValue);
    setIccOptions(iccOptions);

    let updatedFormValues = {
      ...formValues,
      actionTakenSelect: selectedValue,
      actionTakenSelectedItem: actionTakenSelectedItemValue,
      iccSelect: initObjects.icc,
      iccSelectedItem: initIccSelectedItem,
      iccViewEnabled: iccViewEnabled,
      iccEnabled: iccEnabled,//TODO check
    };
    if (!iccViewEnabled) {
      updatedFormValues = {
        ...updatedFormValues,
        iccSelect: iccOptions?.[0]?.resultTypeID,
        iccSelectedItem: iccOptions?.[0],
      }
    }


    UtilityLibrary.updateArrayStateValues(setFormValues, updatedFormValues);
    const requiredAsterisks = calculateRequiredAsterisks(updatedFormValues, caseValidationType);
    setRequiredFieldLabels(requiredAsterisks);
  } catch (error) {
    await LoggerUtility.ManageErrorLogging(error, errorFormName, "handleActionTakenChange");
  }
};

const handleIccChange = async (e, newValue, reason) => {
  try {
    let selectedValue = "";
    let iccSelectedItemValue = initIccSelectedItem;

    if (reason !== "clear" && newValue) {
        selectedValue = newValue.resultTypeID;
        iccSelectedItemValue = newValue;
    }
    if (reason === "clear" || selectedValue === "") {
      const updatedFormValues = {
        ...formValues,
        iccSelect: initObjects.icc,
        iccSelectedItem: initIccSelectedItem,
      };

      UtilityLibrary.updateArrayStateValues(setFormValues, updatedFormValues);

      formFieldsEnableLogic(dispatch, FormFieldEnableType.ActionTakenSelect.ID);

      setRequiredFieldLabels(
        calculateRequiredAsterisks(updatedFormValues, caseValidationType)
      );

      return;
    }

    formFieldsEnableLogic(dispatch, FormFieldEnableType.IccSelect.ID);

    const updatedFormValues = {
      ...formValues,
      iccSelect: selectedValue,
      iccSelectedItem: iccSelectedItemValue,
    };


    UtilityLibrary.updateArrayStateValues(setFormValues, updatedFormValues);
    const requiredAsterisks = calculateRequiredAsterisks(updatedFormValues, caseValidationType);
    setRequiredFieldLabels(requiredAsterisks);
  } catch (error) {
        await LoggerUtility.ManageErrorLogging(error, errorFormName, "handleIccChange");
    }
};

  const onLoadNarrativeForWorkflow = async (workflow) => {
    try {
      let narrativeText = "";
      
      // Skip narrative generation for No Case workflows
      if (formValues.noCaseChk) {

        if (narrativeForStateRef.current) {
          narrativeForStateRef.current.setValue("");
        }
        
        const syntheticEvent = {
          target: {
            name: 'narrativeForStateTxt',
            value: ""
          }
        };
        await handleNarrativeChange(syntheticEvent);
        return "";   // ← was just: return;
      }
      
      const loginUserInfo = sessionService.getUser();
      const loginLegacyUserID = loginUserInfo ? loginUserInfo.legacyUserID : -123456;
      const loginStateID = loginUserInfo ? loginUserInfo.stateID : -123456;
      const loginSiteID = loginUserInfo ? loginUserInfo.siteID : -123456;
      const userLogin = JSON.parse(SessionManager.GetValueFromSession(SessionManager.UserLoginDetailsKey));
      
      const paramsForRequest = {
        ytracSiteID: loginSiteID,
        workflowTypeName: workflow?.workflowName,
        fKM_NoteTemplatedID: formValues?.iccSelectedItem?.fkM_NoteTemplateID,
        actionTypeName: formValues?.iccSelectedItem?.resultTypeName || "",
        userLoginID: loginLegacyUserID,
        masterServerDBSchema,
        yTracSite_StateID: loginStateID,
        workflowStartID: workflow?.startWorkflowStepID || 0,
        loginName: userLogin ? userLogin.loginName?.toLowerCase() : "",
        SelectedCategory: formValues?.categorySelectedItem?.categoryTypeName,
        SelectedSubCategory: formValues?.subCategorySelectedItem?.subCategoryTypeName,
        SystemTimeZoneName: timeZone
      };

      const narrativeApiData = await ApiManager.GetWorkflowNarrative(paramsForRequest);
      if (narrativeApiData && narrativeApiData.length > 0) {
        narrativeText = narrativeApiData[0];
      }

     const previousNarrative = (narrativeForStateRef.current?.getValue?.() ?? "").trim();
      const newNarrative = (narrativeText ?? "").trim();

      let combinedNarrative;
      if (!previousNarrative) {
        combinedNarrative = newNarrative;
      }
      else if (!newNarrative) {
        combinedNarrative = previousNarrative;
      }
      else {
        combinedNarrative = `${previousNarrative}, ${newNarrative}`;
      }

      if (narrativeForStateRef.current) {
        narrativeForStateRef.current.setValue(combinedNarrative);
      }
      
      // Manually trigger the handleNarrativeChange to update form state and validation
      const syntheticEvent = {
        target: {
          name: 'narrativeForStateTxt',
          value: combinedNarrative
        }
      };
      await handleNarrativeChange(syntheticEvent);

      console.log('Narrative: ' + narrativeText);
      return newNarrative;  // ← add this
    }
    catch (error) {
      throw LoggerUtility.ProcessErrorData(error, "onLoadNarrativeForWorkflow", {});
    }
  };

  const handleCancelConfirm = async () => {
    try {
      timerRef.current.resetTimer();
      setWalkinStartTime(null);
      clearFormFields(FormActionType.Cancel.ID);
      clearCaseTabInfo();

    }
    catch (error) {
      await LoggerUtility.ManageErrorLogging(error, errorFormName, "handleCancelConfirm");
    }
  };

  const handleOpenConfirmDialog = () => {
    setConfirmDialogOpen(true);
  };
  
  const handleCloseConfirmDialog = () => {
    setConfirmDialogOpen(false);
  };
  
  const handleDialogConfirmAction = async (dialogContentID) => {
    try {
      switch (dialogContentID) {

        case EnumUtility.ConfirmDialogType.NewWalkInCancel.ID:
          handleCancelConfirm();
          break;

        case EnumUtility.ConfirmDialogType.DuplicateActionAddToList.ID:
          await proceedToAddToList();
          break;

        case EnumUtility.ConfirmDialogType.SubmitCaseWithNoNarrative.ID:
          await proceedWithSave();
          break;

        default:
          break;
      }

      setConfirmDialogOpen(false);
    }
    catch (error) {
      await LoggerUtility.ManageErrorLogging(error, errorFormName, "handleDialogConfirmAction");
    }
  };

  const handleConfirmationDialog = async (dialogContent) => {
    setConfirmationDialogContent(dialogContent);
    handleOpenConfirmDialog();
  };
  
  const proceedToAddToList = async (skipReset = false) => {
  try {
    const workflow = workflowData ?? null;

    const noteTemplateID =
      formValues.iccSelectedItem?.fkM_NoteTemplateID === 0
        ? ClientType.NoteTemplate.Customer_Service_Mainframe_Narrative
        : formValues.iccSelectedItem?.fkM_NoteTemplateID;

    const listOfAction = {
      id: crypto.randomUUID(),
      Action: formValues?.iccSelectedItem?.resultTypeName,
      NextActionType: workflow?.workflowStepName ?? "N/A",
      NextActionTypeID: workflow?.startWorkflowStepID ?? 0,
      DaysToNext: workflow?.initialIntervalValue ?? 0,
      Queue: workflow?.departmentName ?? "N/A",
      Workflow: workflow?.workflowName ?? "N/A",
      WorkflowType: workflow?.workflowTypeName ?? "N/A",
      ContactActivity: {
        systemTimeZoneName: timeZone,
        firstName: formValues.firstNameTxt,
        lastName: formValues.lastNameTxt,
        channelTypeID: formValues?.channelSelectedItem?.channelTypeID,
        categoryTypeID: formValues?.categorySelectedItem?.categoryTypeID,
        subCategoryTypeID: formValues?.subCategorySelectedItem?.subCategoryTypeID,
        resultTypeID: formValues?.iccSelectedItem?.resultTypeID,
        companyName: formValues.companyNameTxt,
        contactID: formValues?.contactID,
        fkm_YTracSiteID: siteID,
        fkm_UserLoginID: userLoginID,
        caseNumber: formValues?.caseNumberTxt,
        cpFirstName: formValues?.cpFirstName || "",
        cpLastName: formValues?.cpLastName || "",
        ncpFirstName: formValues?.ncpFirstName || "",
        ncpLastName: formValues?.ncpLastName || "",
        yTracCaseID: formValues?.yTracCaseID,
        contactRoleID: formValues?.roleSelectedItem?.contactRoleID,
      },
      IsNonCaseAction: formValues?.noCaseChk,
      CaseSearchOK: true,
      CaseNumber: formValues?.caseNumberTxt,
      Category: formValues?.categorySelectedItem?.categoryTypeName,
      SubCategory: formValues?.subCategorySelectedItem?.subCategoryTypeName,
      SelectedContactRole: formValues?.roleSelectedItem?.contactRoleName,
      SelectedResult: formValues?.iccSelectedItem?.resultTypeName,
      NoteTemplateID: noteTemplateID,
      ContactID: formValues?.contactID,
      NarrativeText: "",
      IsShortCircuit: !!formValues?.roleSelectedItem?.enableShortCircuit,
      actionDate: UtilityLibrary.formatDate(UtilityLibrary.getCurrentDateTimeForTimeZone(timeZone), "MM/DD/YYYY"),
      actionTime: UtilityLibrary.formatDate(UtilityLibrary.getCurrentDateTimeForTimeZone(timeZone), "hh:mm:ss A")
    };

    const loginUserInfo = sessionService.getUser();
    const loginLegacyUserID = loginUserInfo ? loginUserInfo.legacyUserID : -123456;
    const paramsForG4Narr = {
      YTracSiteID: loginUserInfo ? loginUserInfo.siteID : -123456,
      NoteTemplateID: formValues?.iccSelectedItem?.fkM_NoteTemplateID,
    };

    const narrativeG4 = await UtilityLibrary.NarrativeBuilder(paramsForG4Narr, listOfAction);
    listOfAction.NarrativeText = narrativeG4 ?? "";

    setResultsListOfActions((prev) => [...prev, listOfAction]);

    let updatedFormValues = formValues;

    if (!skipReset) {
      setAreDropdownsLocked(false);

      updatedFormValues = {
        ...formValues,
        roleSelect: formValues.roleSelect,
        roleSelectedItem: formValues.roleSelectedItem,
        firstNameTxt: formValues.firstNameTxt,
        lastNameTxt: formValues.lastNameTxt,
        companyNameTxt: formValues.companyNameTxt,
        categorySelect: initObjects.category,
        categorySelectedItem: initCategorySelectedItem,
        subCategorySelect: initObjects.subCategory,
        subCategorySelectedItem: initSubCategorySelectedItem,
        actionTakenSelect: initObjects.actionTaken,
        actionTakenSelectedItem: initActionTakenSelectedItem,
        iccSelect: initObjects.icc,
        iccSelectedItem: initIccSelectedItem,
        iccViewEnabled: false,
        iccEnabled: false,
        tracNoteRequired: !!(formValues.noCaseChk || isCaseConfidentialNoPermission),
      };

      UtilityLibrary.updateArrayStateValues(setFormValues, updatedFormValues);
      formFieldsEnableLogic(dispatch, FormFieldEnableType.CategorySelect.ID);
    }

    const nextQueuedActionsCount = resultsListOfActions.length + 1;
    const requiredAsterisks = calculateRequiredAsterisks(
      updatedFormValues,
      caseValidationType,
      nextQueuedActionsCount
    );
    setRequiredFieldLabels(requiredAsterisks);

    setLastAddedItemId(listOfAction.id);
    dispatch({
      type: "SET_FIELD_STATE",
      payload: { field: "addToListBtn", isEnabled: false },
    });
  } catch (error) {
    await LoggerUtility.ManageErrorLogging(error, errorFormName, "proceedToAddToList");
  }
};

const handleAddToListClick = async (event) => {
    try {

      //check for an existing cat/subcat/action, warn user if there is a dupe 
      const existing = resultsListOfActions.find(a => a.Category=== formValues.categorySelectedItem?.categoryTypeName
        && a.SubCategory === formValues.subCategorySelectedItem?.subCategoryTypeName
        && a.Action === formValues.iccSelectedItem?.resultTypeName);
      
      if (!existing) {
        await proceedToAddToList();
        return;
      }
      
      const messageContentID = ClientType.MessageContent.U_UI7037_Duplicate_Action;  
      const messageContent = await SharedService.getMessageContent(messageContentID)
      const warningMessage = { ID: EnumUtility.ConfirmDialogType.DuplicateActionAddToList.ID, 
        NotificationType: 'warning', 
        Message: messageContent?.messageText?.replace(`{0}`, 
          formValues.iccSelectedItem?.resultTypeName).replace(/\\n/g, "\u00A0\n") ?? "" 
      };
      handleConfirmationDialog(warningMessage);
    }
  catch (error) {
    await LoggerUtility.ManageErrorLogging(error, errorFormName, "handleAddToListClick");
  }
  };

const handleNarrativeChange = async (event) => {
  try {
    const { name, value } = event.target;
    const updatedFormValues = { ...formValues, [name]: value };
    setFormValues(updatedFormValues);
    // updateArrayStateValues(setPostNewDocData, { narrative: value });//TODO
  }
  catch (error) {
    await LoggerUtility.ManageErrorLogging(error, errorFormName, "handleNarrativeChange");
  }
  };

   const handleNotesChange = async (event) => {
    try {
      const { name, value } = event.target;
      const updatedFormValues = { ...formValues, [name]: value };
      setFormValues(updatedFormValues);
      debouncedValidation(updatedFormValues, caseValidationType);
    }
    catch (error) {
      await LoggerUtility.ManageErrorLogging(error, errorFormName, "handleNotesChange");
    }
  };
  

  const handleCancelClick = async () => {
    try {
        const hasUnsavedWork =
          !!formValues?.caseNumberTxt || formValues?.noCaseChk === true ||                       
          !isEmptyDropdown(formValues?.roleSelect) || !isEmptyDropdown(formValues?.categorySelect) ||
          !isEmptyDropdown(formValues?.subCategorySelect) || !isEmptyDropdown(formValues?.actionTakenSelect) ||
          !isEmptyDropdown(formValues?.iccSelect) || !!formValues?.firstNameTxt?.trim() ||  !!formValues?.lastNameTxt?.trim() ||                  
          !!formValues?.companyNameTxt?.trim() || !!noteForActionRef?.current?.getValue() ||          
          !!narrativeForStateRef?.current?.getValue() || (resultsListOfActions?.length ?? 0) > 0; 
      if (hasUnsavedWork) {
          handleConfirmationDialog(EnumUtility.ConfirmDialogType.NewWalkInCancel);
        }
        else {
          await clearFormFields(FormActionType.Cancel.ID);
          clearCaseTabInfo();
          setIsEditable(false);
        }
      }
    catch (error) {
      await LoggerUtility.ManageErrorLogging(error, errorFormName, "handleCancelClick");
    }
  };


  const clearFormStateValues = () => {
    try {
      setFormValues((prevValues) => ({
        ...prevValues,
        ...initialFormValues
      }));
    }
    catch (error) {
      throw LoggerUtility.ProcessErrorData(error, "clearFormStateValues", {});
    }
  };

  const clearNewWorkflowForm = () => {
    try {
      clearFormStateValues();
      noteForActionRef.current?.clear();
      narrativeForStateRef.current?.clear();
      setResultsListOfActions([]);
      setRoleOptions([]);
      setCategoryOptions([]);
      setSubCategoryOptions([]);
      setActionTakenOptions([]);
      setIccOptions([]);
      setAreDropdownsLocked(false);

      const validationType = EnumUtility.CaseType.Case;
      setCaseValidationType(validationType);
      setFormValues((prev) => ({
        ...prev,
        tracNoteRequired: false
      }));
      debouncedValidation(initialFormValues, validationType);
    } catch (error) {
      throw LoggerUtility.ProcessErrorData(error, "clearNewWorkflowForm", {});
    }
  };

  const FormActionType = {
  Case: { ID: 100, name: "Case" },
  NoCaseChck: { ID: 101, name: "NoCaseCheck" },
  Save: { ID: 102, name: "Save" },
  SaveAnother: { ID: 103, name: "SaveAnother" },
  Cancel: { ID: 104, name: "Cancel" },
  Skip: { ID: 105, name: "Skip" }
  };

const resetCommonState = () => {
  noteForActionRef.current?.clear();
  narrativeForStateRef.current?.clear();
  setResultsListOfActions([]);
  };

  const clearOptions = () => {
    setRoleOptions([]);
    setCategoryOptions([]);
    setSubCategoryOptions([]);
    setActionTakenOptions([]);
    setIccOptions([]);
  }

  function clearFormFields(formActionType, resetOptions = false) {
    try {
      let validationType = EnumUtility.CaseType.Case;
      let updatedFormValues = {};
      const baseResetForm = {
        ...initialFormValues,
        channelSelect: formValues.channelSelect,
        channelSelectedValue: formValues.channelSelectedValue
      };

      if (resetOptions) clearOptions();

      switch (formActionType) {
        case FormActionType.Save.ID:
        case FormActionType.Cancel.ID:
        case FormActionType.Skip.ID:
          updatedFormValues = {
            ...baseResetForm,
            tracNoteRequired: false
          };
          break;

        case FormActionType.SaveAnother.ID:
          updatedFormValues = {
            ...baseResetForm,
            channelSelect: formValues.channelSelect,
            channelSelectedValue: formValues.channelSelectedValue,
            firstNameTxt: formValues.firstNameTxt,
            lastNameTxt: formValues.lastNameTxt,
            companyNameTxt: formValues.companyNameTxt,
            contactNamesEnabled: true,
            tracNoteRequired: false
          };
          break;

        case FormActionType.NoCaseChck.ID:
          updatedFormValues = {
            ...baseResetForm,
            firstNameTxt: formValues.firstNameTxt,
            lastNameTxt: formValues.lastNameTxt,
            companyNameTxt: formValues.companyNameTxt,
            contactNamesEnabled: true,
            tracNoteRequired: true
          };
          validationType = EnumUtility.CaseType.NoCase;
          break;

        case FormActionType.Case.ID:
          updatedFormValues = {
            ...baseResetForm,
            tracNoteRequired: false
          };
          break;

        default:
          updatedFormValues = {
            ...baseResetForm,
            tracNoteRequired: false
          };
          break;
      }

      updateArrayStateValues(setFormValues, updatedFormValues);
      setCaseValidationType(validationType);
      debouncedValidation(updatedFormValues, validationType);
      resetCommonState();
      setAreDropdownsLocked(false);
      setPendingAutoAdd(false);
    } catch (error) {
      throw LoggerUtility.ProcessErrorData(error, "clearFormFields", {});
    }
  }

  const handleSkipClick = async(event) => {
    try {
      timerRef.current.resetTimer();
      setWalkinStartTime(null);
      await clearFormFields(FormActionType.Skip.ID);
      clearCaseTabInfo();
      caseNumberRef.current?.setValidationMessage("");
      changeCaseNumberIconsDisable({ search: false, copy: false, clear: false });
      setIsCaseConfidentialNoPermission(false);
      formFieldsEnableLogic(dispatch, FormFieldEnableType.NewWalkInClick.ID);
    }
    catch (error) {      
      await LoggerUtility.ManageErrorLogging(error, errorFormName, "handleSkipClick");
    }
  };

  const handleSaveAnotherClick = async (event) => {
    setIsPageLoading(true);
     try {
  
        const currentDateTime = new Date().toISOString();
        const loginUserInfo = sessionService.getUser();
        const loginLegacyUserID = loginUserInfo ? loginUserInfo.legacyUserID : -123456;
        const loginStateID = loginUserInfo ? loginUserInfo.stateID : -123456;
        const loginSiteID = loginUserInfo ? loginUserInfo.siteID : -123456;

        const isMainframeIntegration = UtilityLibrary.DetermineIfMainframeHost();
  
        if (isMainframeIntegration && formValues.noCaseChk === false) {
  
            const canContinue = await UtilityLibrary.checkMainframeConnectivityWithErrorHandling(
                configLibrary.apiMainframeUrl, 
                handleNotificationDialog
            );
  
            if (!canContinue) {
                setIsPageLoading(false);
                return; // Exit early if mainframe is not reachable
            }
  
        }
  
        const narrativeText = narrativeForStateRef.current ? narrativeForStateRef.current?.getValue() : "";
        
        // Check if this is a case (not no-case) and narrative is empty
        const isNarrativeEmpty = !narrativeText || narrativeText.trim() === "";
        
        if (!formValues.noCaseChk && isNarrativeEmpty) {
          setIsPageLoading(false); // Turn off loading since we're showing dialog
          // Show confirmation dialog for submitting case with no narrative
          handleConfirmationDialog(EnumUtility.ConfirmDialogType.SubmitCaseWithNoNarrative);
          return;
        }
  
        // Proceed with save
        await proceedWithSave();
       
        clearFormFields(FormActionType.SaveAnother.ID);
        clearCaseTabInfo();
        
        formFieldsEnableLogic(dispatch, FormFieldEnableType.SaveAnother.ID);
        setIsSkipVisible(true);
      }
      catch (error) {
        await LoggerUtility.ManageErrorLogging(error, errorFormName, "handleSaveClick");
      }
    finally {
      setIsPageLoading(false);
    }
  };

  const handleSaveClick = async (event) => {
      setIsPageLoading(true);
      try {
  
        const currentDateTime = new Date().toISOString();
        const loginUserInfo = sessionService.getUser();
        const loginLegacyUserID = loginUserInfo ? loginUserInfo.legacyUserID : -123456;
        const loginStateID = loginUserInfo ? loginUserInfo.stateID : -123456;
        const loginSiteID = loginUserInfo ? loginUserInfo.siteID : -123456;
  
        const isMainframeIntegration = UtilityLibrary.DetermineIfMainframeHost();

        if (isMainframeIntegration && formValues.noCaseChk === false) {
  
            const canContinue = await UtilityLibrary.checkMainframeConnectivityWithErrorHandling(
                configLibrary.apiMainframeUrl, 
                handleNotificationDialog
            );
  
            if (!canContinue) {
                setIsPageLoading(false);
                return; // Exit early if mainframe is not reachable
            }
  
        }
  
        const narrativeText = narrativeForStateRef.current ? narrativeForStateRef.current?.getValue() : "";
        
        // Check if this is a case (not no-case) and narrative is empty
        const isNarrativeEmpty = !narrativeText || narrativeText.trim() === "";
        
        if (!formValues.noCaseChk && isNarrativeEmpty) {
          setIsPageLoading(false); // Turn off loading since we're showing dialog
          // Show confirmation dialog for submitting case with no narrative
          handleConfirmationDialog(EnumUtility.ConfirmDialogType.SubmitCaseWithNoNarrative);
          return;
        }
  
        // Proceed with save
        await proceedWithSave();
       
        clearFormFields(FormActionType.Save.ID);
        clearCaseTabInfo();

        //formFieldsEnableLogic(dispatch, FormFieldEnableType.SaveBtn.ID);
        formFieldsEnableLogic(dispatch, FormFieldEnableType.NewWalkInClick.ID);
      }
      catch (error) {
        await LoggerUtility.ManageErrorLogging(error, errorFormName, "handleSaveClick");
      }
  };
  
  const proceedWithSave = async () => {
    try {
      setIsPageLoading(true);
      const narrativeText = narrativeForStateRef.current ? narrativeForStateRef.current?.getValue() : "";
      await copyToClipboard(narrativeText);
      await simulateAsyncDelay();

      // Re-fetch the Contact ID right before saving instead of trusting the value grabbed at case-search/No-Case time .
      let contactIDForSave = formValues?.contactID;
      try {
        const ContactNextIDApiUrl = await ApiEndpoints.ContactNextIDApiUrl();
        const refreshedContactID = await ApiManager.GetDataWithParamsFromAPI(ContactNextIDApiUrl, { siteID, masterServerDBSchema }, { headers: { "Cache-Control": "no-cache", Pragma: "no-cache" } }, 101);
        if (refreshedContactID !== undefined && refreshedContactID !== null) {
          contactIDForSave = refreshedContactID;
          updateArrayStateValues(setFormValues, { contactID: refreshedContactID });
        }
      }
      catch (contactIDError) {
        await LoggerUtility.ManageErrorLogging(contactIDError, errorFormName, "proceedWithSave_refreshContactID");
      }

        const postNewWorkflowData = {
            WorkflowID : workflowData?.workflowID,
            CaseNumber : formValues?.caseNumberTxt,
            IsNocase : formValues?.noCaseChk ? 1: 0,
            Notes : noteForActionRef.current ? noteForActionRef.current?.getValue() : "",
            Narrative : narrativeText,
            YTracSiteID: siteID,
            MasterServerDBSchema: masterServerDBSchema,
            ActionStartTime: walkinStartTime,
            UserLoginID: userLoginID,
            StartWorkflowStepID: workflowData?.startWorkflowStepID,
            WorkflowMapID: workflowData?.workflowMapID,
            YTracSite_StateID: stateID,
            ModuleID: 1,
            NextStepDueTS: null,
            SystemTimeZoneName: timeZone,
            ContactID: contactIDForSave,
            // keep every queued action's ContactID in sync with the refreshed value above
            QueuedActions: resultsListOfActions.map((action) => ({
              ...action,
              ContactID: contactIDForSave,
              ContactActivity: action.ContactActivity
                ? { ...action.ContactActivity, contactID: contactIDForSave }
                : action.ContactActivity,
            }))
      };
      const postResponseData = await ApiManager.PostNewWorkflowSaveToApi(postNewWorkflowData, true);
      
       if (postResponseData) {
        let messageText = EnumUtility.WorkflowProcess.NewWalkinWorkflowSavedSuccess;
        
        // Show custom success message
        setSuccessMessageText(messageText);
        setShowSuccessMessage(true);

        formFieldsEnableLogic(dispatch, FormFieldEnableType.NewWalkInClick.ID);
        
        // Set flag to focus Case Search field after success message disappears
        setShouldFocusCaseSearch(true);
        timerRef.current.resetTimer();
        setWalkinStartTime(null);
        setAreDropdownsLocked(false);
        setPendingAutoAdd(false);
      }
    }
    catch (error) {//TODO call to mainframe has not been implemented yet
      const errorType = error?.Details?.type;
      if ([ClientType.ErrorType.MainframeDisconnected,
            ClientType.ErrorType.ScraperDisconnected].includes(errorType)) {
        let messageContentID = null;
        if (errorType === ClientType.ErrorType.MainframeDisconnected){
          messageContentID = ClientType.MessageContent.E_MF7049_Lost_Connection_To_State_System
        }
        else{
          messageContentID = ClientType.MessageContent.E_UI7056_Scrapper_Connection_Status;
        }
        const messageContent = await SharedService.getMessageContent(messageContentID);
        handleNotificationDialog({ NotificationType: 'error', Message: messageContent?.messageText });
      }
      else {
        await LoggerUtility.ManageErrorLogging(error, errorFormName, "handleSaveClick");
      }
    }
    finally {
      setIsPageLoading(false); // Disable the loader
    }
  }
  const copyToClipboardFallback = (text) => {
    // Create a temporary textarea element
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.style.position = 'fixed'; // Avoid scrolling to the bottom
    textarea.style.top = '0';
    textarea.style.left = '0';
    textarea.style.opacity = '0'; // Make it invisible
    document.body.appendChild(textarea);

    // Select and copy the text
    textarea.select();
    textarea.setSelectionRange(0, text.length); // Ensure all text is selected
    try {
      const successful = document.execCommand('copy');
      if (successful) {
        console.log('Text successfully copied to clipboard');
        //alert('Text copied to clipboard!'); // Notify the user
      } else {
        throw new Error('Failed to copy text using execCommand');
      }
    } catch (err) {
      console.error('Failed to copy text: ', err);
      //alert('Failed to copy text. Please try again.'); // Notify the user
    }

    // Clean up
    document.body.removeChild(textarea);
  };
  
  const copyToClipboard = async (textToCopy) => {
    //const textToCopy = textareaRef.current.value;

    if (navigator.clipboard) {
      await navigator.clipboard.writeText(textToCopy)
        .then(() => {
          console.log('Text successfully copied to clipboard');
          //alert('Text copied to clipboard!'); // Optional: Notify the user
        })
        .catch((err) => {
          console.error('Failed to copy text: ', err);
          // Fallback to execCommand if Clipboard API fails
          copyToClipboardFallback(textToCopy);
        });
    } else {
      // Use fallback if Clipboard API is not supported
      copyToClipboardFallback(textToCopy);
    }
  };

  useEffect(() => {
    const {channelSelectedItem, channelSelect, categorySelectedItem, categorySelect, subCategorySelectedItem, subCategorySelect}= formValues;
    const isValidSelection = (val) => {
      if (val == null) return false;
      if (typeof val === "number") return val > 0;
      if (typeof val === "object") return val.id != null && val.id > 0;
      return false;
    };
    
    if (isValidSelection(channelSelect) && isValidSelection(categorySelect) && isValidSelection(subCategorySelect)) {
      const fetchResults = async () => {
        try {
          const resultListTypeID = resultListMapData.find(rlm =>
            (rlm.channelTypeID === channelSelectedItem?.channelTypeID || rlm.channelTypeID == null) &&
            (rlm.categoryTypeID === categorySelectedItem?.categoryTypeID || rlm.categoryTypeID == null) &&
            (rlm.subCategoryTypeID === subCategorySelectedItem?.subCategoryTypeID || rlm.subCategoryTypeID == null))?.resultListTypeID;
          const resultListTypesData = await ApiManager.GetResultListTypeData({ resultListTypeID });
          let groupTypes = resultListTypesData?.[0]?.[0]?.resultTypes.flatMap(rt => rt.resultGroupTypes || []);
          setResultTypes(resultListTypesData?.[0]?.[0]?.resultTypes)
          groupTypes = groupTypes.filter((item, index, self) => index === self.findIndex(t => t.resultGroupTypeID === item.resultGroupTypeID))
          setActionTakenOptions(groupTypes);
        } catch (err) {
          console.error("Error fetching results:", err);
        }
      };
      fetchResults();
    }
  }, [formValues?.channelSelect, formValues?.categorySelect, formValues?.subCategorySelect]);
  
// Handler to show message after processing document in Right Pane
const handleMessageUpdate = (leftMessage) => {
    setLeftPaneMessage(leftMessage);
  };

const leftPaneContent = (
    <div className="content-main-container">
      {!isEditable && (
        <div className="banner-blue">
          <div className="button-group">
            <div className="base-wrapper">
              {!(formFieldAccess.newWalkInBtn?.hide ?? false) && (
                <div className="base-2">
                  <img
                    className="masked-icon"
                    alt="Masked icon"
                    src="/images/new_walk-in_icon.png"
                  />
                  <Button
                    id="newWalkInBtn"
                    name="newWalkInBtn"
                    className="button-2"
                    onClick={handleNewWalkInClick}
                    disabled={!(formFieldAccess.newWalkInBtn?.write ?? true)}
                  >
                    NEW WALK-IN
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      <div className="pane-wrap-container">
        <div
          className={`${isEditable ? "pane-main-container" : "pane-main-container-2 greyed-out"}`}
        >
          <div
            className={`${isPageLoading ? " greyed-out" : isEditable ? "form-section editable" : "form-section-2 "}`}
          >
            <div className="text-wrapper-header-bold">New Walk-In</div>
            <div
              className={`${isEditable ? "form-main-container" : "form-main-container-2"}`}
            >
              <div className="form-content">
                <div className="form-dynamic-content">
                  <form className="form-dynamic-grid">
                    {/* Field 1 */}
                    <div className="form-row">
                      <div className="form-group">
                        {!(formFieldAccess.caseNumberTxt?.hide ?? false) && (
                          <TextFieldWithIconButton
                            ref={caseNumberRef}
                            nameForField="caseNumberTxt"
                            onIconButtonClick={handleCaseSearch}
                            onClearButtonClick={handleCaseClear}
                            onTextChange={handleCaseChange}
                            valueForField={formValues.caseNumberTxt}
                            labelForField="Case Search"
                            iconsTooltipText={{
                              search: "Search Case",
                              copy: "Copy Case",
                              clear: "Clear Case",
                            }}
                            iconsDisabled={caseNumberIconsDisabled}
                            isDisabled={formValues.noCaseChk || !formState.caseNumberTxt || areDropdownsLocked}
                            requiredFieldLabel={requiredFieldLabels.caseNumberTxt}
                          />
                        )}
                      </div>
                      <div className="form-group">
                        {!(formFieldAccess.noCaseChk?.hide ?? false) && (
                          <div>
                            <FormControlLabel
                              name="noCaseChk"
                              control={<Checkbox />}
                              label="No Case"
                              checked={formValues.noCaseChk}
                              onChange={handleNoCaseChange}
                              disabled={!formState.noCaseChk || areDropdownsLocked}
                            />
                            <label>
                              <span
                                style={{
                                  width: "10px",
                                  display: "inline-block",
                                  textAlign: "center",
                                  color: "red",
                                }}
                              >
                                {requiredFieldLabels.noCaseChk}
                              </span>
                            </label>
                          </div>
                        )}
                      </div>
                    </div>
                    <div
                      className="text-wrapper-header-bold-2"
                      style={{ marginTop: "5px" }}
                    >
                      Walk-In Details
                    </div>
                    <div className="form-row">
                       <div className="form-group">
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                          }}
                        >
                          <FormControl fullWidth>
                            <Autocomplete
                              id="channelSelect"
                              options={channelOptions}
                              getOptionLabel = {(option) => option.channelTypeName || ""}
                              isOptionEqualToValue={(option, value) => option.channelTypeID === value?.channelTypeID}
                              value={formValues.channelSelectedItem?.channelTypeID && formValues.channelSelectedItem?.channelTypeID !== -123456 ? 
                              channelOptions.find((opt) => opt.channelTypeID === formValues.channelSelectedItem.channelTypeID) || null : null}
                              disabled
                              onChange={(event, newValue, reason) => {
                                const modifiedEvent = {
                                  target: {
                                    name: "channelSelect",
                                  },
                                };
                                handleChannelChange(modifiedEvent, newValue, reason);
                              }}
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  label="Channel"
                                  variant="outlined"
                                  InputLabelProps={{ shrink: true }}
                                />
                              )}
                            />
                          </FormControl>
                          <label>
                            <span
                              style={{
                                width: "10px",
                                display: "inline-block",
                                textAlign: "center",
                                color: "red",
                              }}
                            >
                              {requiredFieldLabels.channelSelect}
                            </span>
                          </label>
                        </div>
                      </div>
                      <div className="form-group">
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                          }}
                        >
                          <FormControl fullWidth>
                            <Autocomplete
                              id="roleSelect"
                              options={roleOptions}
                              getOptionLabel = {(option) => option.contactRoleName || ""}
                              value={formValues.roleSelectedItem?.contactRoleID && formValues.roleSelectedItem?.contactRoleID !== -123456 ? 
                              roleOptions.find((opt) => opt.contactRoleID === formValues.roleSelectedItem.contactRoleID) || null : null}
                            disabled={!formState.roleSelect || roleOptions.length <= 1 || areDropdownsLocked}
                              onChange={(event, newValue, reason) => {
                                const modifiedEvent = {
                                  target: {
                                    name: "roleSelect",
                                  },
                                };
                                handleRoleChange(modifiedEvent, newValue, reason);
                              }}
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  label="Role"
                                  variant="outlined"
                                  InputLabelProps={{ shrink: true }}
                                />
                              )}
                            />
                          </FormControl>
                          <label>
                            <span
                              style={{
                                width: "10px",
                                display: "inline-block",
                                textAlign: "center",
                                color: "red",
                              }}
                            >
                              {requiredFieldLabels.roleSelect}
                            </span>
                          </label>
                        </div>
                      </div>
                    </div>
                    <div className="form-row">
                      <div className="form-group">
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                          }}
                        >
                          <FormControl fullWidth>
                            <TextField
                              label="First Name"
                              InputLabelProps={{ shrink: true }}
                              id="firstNameTxt"
                              name="firstNameTxt"
                              value={formValues.firstNameTxt}
                              disabled={ !formValues.contactNamesEnabled}
                              onChange={handleNameChange}
                            />
                          </FormControl>
                          <label>
                            <span
                              style={{
                                width: "10px",
                                display: "inline-block",
                                textAlign: "center",
                                color: "red",
                              }}
                            >
                              {requiredFieldLabels.firstNameTxt}
                            </span>
                          </label>
                        </div>
                      </div>
                      <div className="form-group">
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                          }}
                        >
                          <FormControl fullWidth>
                            <TextField
                              label="Last Name"
                              InputLabelProps={{ shrink: true }}
                              id="lastNameTxt"
                              name="lastNameTxt"
                              value={formValues.lastNameTxt}
                              onChange={handleNameChange}
                              disabled={ !formValues.contactNamesEnabled}
                            />
                          </FormControl>
                          <label>
                            <span
                              style={{
                                width: "10px",
                                display: "inline-block",
                                textAlign: "center",
                                color: "red",
                              }}
                            >
                              {requiredFieldLabels.lastNameTxt}
                            </span>
                          </label>
                        </div>
                      </div>
                    </div>

                    <div className="form-row">
                      <div
                        className="col-yt-1"
                        style={{
                          display: "grid",
                          gridTemplateColumns: "1fr 1fr",
                          gap: "16px",
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                          }}
                        >
                          <FormControl fullWidth>
                            <TextField
                              label="Company"
                              id="companyNameTxt"
                              name="companyNameTxt"
                              InputLabelProps={{ shrink: true }}
                              value={formValues.companyNameTxt}
                              onChange={handleCompanyNameChange}
                            />
                          </FormControl>
                          <label>
                            <span
                              style={{
                                width: "10px",
                                display: "inline-block",
                                textAlign: "center",
                                color: "red",
                              }}
                            >
                              {requiredFieldLabels.companyNameTxt}
                            </span>
                          </label>
                        </div>
                      </div>
                    </div>
                    <div className="form-row">
                      <div className="form-group">
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                          }}
                        >
                          <FormControl fullWidth>
                            <Autocomplete
                              id="categorySelect"
                              options={categoryOptions}
                              getOptionLabel = {(option) => option.categoryTypeName || ""}
                              value={formValues.categorySelectedItem?.categoryTypeID && formValues.categorySelectedItem?.categoryTypeID !== -123456 ? 
                              categoryOptions.find((opt) => opt.categoryTypeID === formValues.categorySelectedItem?.categoryTypeID) || null : null}
                              disabled={!formState.categorySelect || categoryOptions.length <= 1 || areDropdownsLocked}
                              onChange={(event, newValue, reason) => {
                                const modifiedEvent = {
                                  target: {
                                    name: "categorySelect",
                                  },
                                };
                                handleCategoryChange(modifiedEvent, newValue, reason);
                              }}
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  label="Category"
                                  variant="outlined"
                                  InputLabelProps={{ shrink: true }}
                                />
                              )}
                            />
                          </FormControl>
                          <label>
                            <span
                              style={{
                                width: "10px",
                                display: "inline-block",
                                textAlign: "center",
                                color: "red",
                              }}
                            >
                              {requiredFieldLabels.categorySelect}
                            </span>
                          </label>
                        </div>
                      </div>
                      <div className="form-group">
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                          }}
                        >
                          <FormControl fullWidth>
                             <Autocomplete
                              id="subCategorySelect"
                              options={subCategoryOptions}
                              getOptionLabel = {(option) => option.subCategoryTypeName || ""}
                              value={formValues.subCategorySelectedItem?.subCategoryTypeID && formValues.subCategorySelectedItem?.subCategoryTypeID !== -123456 ? 
                              subCategoryOptions.find((opt) => opt.subCategoryTypeID === formValues.subCategorySelectedItem.subCategoryTypeID) || null : null}
                              disabled={!formState.subCategorySelect || subCategoryOptions.length <= 1 || areDropdownsLocked}
                              onChange={(event, newValue, reason) => {
                                const modifiedEvent = {
                                  target: {
                                    name: "subCategorySelect",
                                  },
                                };
                                handleSubCategoryChange(modifiedEvent, newValue, reason);
                              }}
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  label="Sub-Category"
                                  variant="outlined"
                                  InputLabelProps={{ shrink: true }}
                                />
                              )}
                            />
                          </FormControl>
                          <label>
                            <span
                              style={{
                                width: "10px",
                                display: "inline-block",
                                textAlign: "center",
                                color: "red",
                              }}
                            >
                              {requiredFieldLabels.subCategorySelect}
                            </span>
                          </label>
                        </div>
                      </div>
                    </div>
                    <div className="form-row">
                      <div className="form-group">
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                          }}
                        >
                          <FormControl fullWidth>
                             <Autocomplete
                              id="actionTakenSelect"
                              options={actionTakenOptions}
                              getOptionLabel = {(option) => option.resultGroupTypeName || ""}
                              value={formValues.actionTakenSelectedItem?.resultGroupTypeID && formValues.actionTakenSelectedItem?.resultGroupTypeID !== -123456 ? 
                              actionTakenOptions.find((opt) => opt.resultGroupTypeID === formValues.actionTakenSelectedItem?.resultGroupTypeID) || null : null}
                              disabled={!formState.actionTakenSelect || actionTakenOptions.length <= 1 || areDropdownsLocked}
                              onChange={(event, newValue, reason) => {
                                const modifiedEvent = {
                                  target: {
                                    name: "actionTakenSelect",
                                  },
                                };
                                handleActionTakenChange(modifiedEvent, newValue, reason);
                              }}
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  label="Action Taken"
                                  variant="outlined"
                                  InputLabelProps={{ shrink: true }}
                                />
                              )}
                            />
                          </FormControl>
                          <label>
                            <span
                              style={{
                                width: "10px",
                                display: "inline-block",
                                textAlign: "center",
                                color: "red",
                              }}
                            >
                              {requiredFieldLabels.actionTakenSelect}
                            </span>
                          </label>
                        </div>
                      </div>
                    {formValues?.iccViewEnabled ? (<div className="form-group">
                        
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                        }}
                      >
                        <FormControl fullWidth>
                            
                          <Autocomplete
                            id="iccSelect"
                            options={iccOptions}
                            getOptionLabel={(option) => option.resultTypeShortName || ""}
                           value={
                        formValues.iccSelectedItem?.resultTypeID && 
                        formValues.iccSelectedItem?.resultTypeID !== -123456
                          ? iccOptions.find((opt) => opt.resultTypeID === formValues.iccSelectedItem.resultTypeID) || null
                          : null
                      }
                            disabled={!formState.iccSelect || iccOptions.length <= 1 || areDropdownsLocked}
                            onChange={(event, newValue, reason) => {
                              const modifiedEvent = {
                                target: {
                                  name: "iccSelect",
                                },
                              };
                              handleIccChange(modifiedEvent, newValue, reason);
                            }}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label="ICC"
                                variant="outlined"
                                InputLabelProps={{ shrink: true }}
                                disabled={!formValues.iccEnabled}
                              />
                            )}
                          />
                        </FormControl>
                        <label>
                          <span
                            style={{
                              width: "10px",
                              display: "inline-block",
                              textAlign: "center",
                              color: "red",
                            }}
                          >
                            {requiredFieldLabels.iccSelect}
                          </span>
                        </label>
                      </div>
                    </div>
                    ): <div className="form-group"></div>}
                    </div>
                    <div className="form-row">
                      <div className="form-group">
                        <div className="d-flex justify-content-between align-items-center mb-2 form-actions-2">
                          <div className='rightDiv'>
                            {!(formFieldAccess.addToListBtn?.hide ?? false) && (
                              <Button id="addToListBtn" name="addToListBtn" variant='contained' color='ywButtonBlue' className='me-3' onClick={handleAddToListClick} 
                              disabled={
                                !formState.addToListBtn ||
                                (!formValues.caseNumberTxt && !formValues.noCaseChk) ||
                                !(formValues.roleSelectedItem?.contactRoleID > 0) ||
                                !(formValues.firstNameTxt !== "") ||
                                !(formValues.lastNameTxt !== "") ||
                                !(formValues.categorySelectedItem?.categoryTypeID > 0) ||
                                !(formValues.subCategorySelectedItem?.subCategoryTypeID > 0) ||
                                !(formValues?.actionTakenSelectedItem?.resultGroupTypeID > 0) ||
                                (formValues.iccViewEnabled &&
                                  !(formValues?.iccSelectedItem?.resultGroupTypeID > 0))
                              }>
                                  ADD TO LIST</Button>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="form-row">
                      <div className="form-group">
                        {!(formFieldAccess.noteForActionTxt?.hide ?? false) && (
                          <div className="col-12 mb-3 align-items-center justify-center">
                            <TextAreaWithCount
                              ref={noteForActionRef}
                              id="noteForActionTxt"
                              nameForField="noteForActionTxt"
                              labelValue="Y-Trac Note for Action Taken"
                              // initialValue={notesForAction}
                              maxLength={notesCharLimit}
                              placeholder=""
                              onTextChange={handleNotesChange}
                              isDisabled={
                                !(
                                  formFieldAccess.noteForActionTxt?.write ??
                                  true
                                )
                              }
                              requiredFieldLabel={
                                requiredFieldLabels.noteForActionTxt
                              }
                            />
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="form-row">
                      <div className="form-group">
                        {!(
                          formFieldAccess.narrativeForStateTxt?.hide ?? false
                        ) && (
                          <div className="col-12 mb-3 align-items-center justify-center">
                            <TextAreaWithCount
                              ref={narrativeForStateRef}
                              id="narrativeForStateTxt"
                              nameForField="narrativeForStateTxt"
                              labelValue="Narrative for State System"
                              // initialValue={narrativeState}
                              maxLength={narrativeCharLimit}
                              placeholder=""
                              onTextChange={handleNarrativeChange}
                              requiredFieldLabel={requiredFieldLabels.narrativeForStateTxt}
                              isDisabled={!formState.narrativeForStateTxt || (areDropdownsLocked && formValues.noCaseChk)}
                            />
                          </div>
                        )}
                      </div>
                    </div>
                  </form>
                  {/* DataGrid */}
                  {!(formFieldAccess.listOfActionsDgrd?.hide ?? false) && (
                    <div className="data-grid-container">
                      {/* DataGrid for List of Actions */}
                      <div className="yt-button-container">
                        <div
                          className="text-wrapper-header-bold-2"
                          style={{ marginTop: "5px" }}
                        >
                          List of Actions
                        </div>
                      </div>
                      <div
                        style={{
                          marginTop: "2px",
                          height: "100%",
                          width: "100%",
                        }}
                      >
                        {hasListOfActions ? (
                          <DataGrid
                            id="listOfActionsDgrd"
                          name="listOfActionsDgrd"
                          apiRef = {listOfActionsApiRef}
                            rows={resultsListOfActions}
                            columns={listOfActionsGridColumns}
                            rowsPerPageOptions={[5, 10, 15, 20, 25]}
                            pagination
                            pageSizeOptions={[5, 10, 15, 20, 25]}
                            disableSelectionOnClick
                            sx={{
                              "& .MuiDataGrid-row:hover": {
                                cursor: "pointer",
                              },
                              ".MuiTablePagination-displayedRows, .MuiTablePagination-selectLabel":
                                {
                                  "margin-top": "1em",
                                  "margin-bottom": "1em",
                                },
                              "& .MuiDataGrid-virtualScroller": {
                                overflowX: "auto",
                                overflowY: "auto",
                              },
                            }}
                          />
                        ) : (
                          <div className="text-wrapper-subtitle">
                            No action(s) found!
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
              {/* Footer Section */}
              <footer className="form-footer">
                <div className="d-flex justify-content-between align-items-center mb-2  form-actions">
                    <div className="leftDiv">
                      {!(formFieldAccess.cancelBtn?.hide ?? false) && (
                        <Button
                          id="cancelBtn"
                          name="cancelBtn"
                          color="ywButtonWhite"
                          variant="outlined"
                          className="me-3"
                          onClick={handleCancelClick}
                          disabled={!(formFieldAccess.cancelBtn?.write ?? true)}
                        >
                          CANCEL
                        </Button>
                      )}
                      {!(formFieldAccess.leftPaneMessageLbl?.hide ?? false) && (
                      <div className="center-section text-wrapper-message">
                        <div><label id="leftPaneMessageLbl" name="leftPaneMessageLbl">{leftPaneMessage}</label></div>
                        {isTimerVisible &&
                          <div><label className='timer-message'>Timer: {timerView}</label></div>
                        }
                      </div>
                      )}
                    </div>
                    <div className="rightDiv">
                      {!(formFieldAccess.saveBtn?.hide ?? false) && (
                        <Button
                          id="saveBtn"
                          name="saveBtn"
                          variant="contained"
                          color="ywButtonBlue"
                          className="me-3"
                          onClick={handleSaveClick}
                          disabled={!isSaveEnabled}
                        >
                          SAVE + NEW WALK-IN
                        </Button>
                      )}
                      {!(formFieldAccess.saveAnotherBtn?.hide ?? false) && (
                        <Button
                          id="saveAnotherBtn"
                          name="saveAnotherBtn"
                          variant="contained"
                          color="ywButtonBlue"
                          className="me-3"
                          onClick={handleSaveAnotherClick}
                          disabled={!isSaveEnabled || isShortCircuitRoleSelected}
                        >
                          SAVE + ANOTHER
                        </Button>
                      )}
                      {!(formFieldAccess.skipBtn?.hide ?? false) && isSkipVisible && (
                        <Button
                          id="skipBtn"
                          name="skipBtn"
                          variant="contained"
                          color="ywButtonBlue"
                          className="me-3"
                          onClick={handleSkipClick}
                        >
                          SKIP
                        </Button>
                      )}
                    </div>
                </div>
              </footer>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
  const rightPaneContent = (
    <div style={{ width: "100%", height: "100%" }}>
      <CaseInformationPane
        ref={caseInfoRef}
        onCaseSearchComplete={handleCaseSearchComplete}
        pageName={"New Walk-In"}
      />
    </div>
  );

  return (
    <div style={{ height: "100%" }}>
      <ToggleSplitPane
        leftPaneVisibility={true}
        leftPaneTitle={leftPaneTitle}
        leftPaneContent={leftPaneContent}
        rightPaneTitle={rightPaneTitle}
        rightPaneContent={rightPaneContent}
        leftPaneSize="50%"
        onMessageUpdate={handleMessageUpdate}
        pageName={'New Walk-In'}
      ></ToggleSplitPane>
      {/* Include the loader component */}
      {/* <PageLoader isPageLoading={isPageLoading} /> */}

      {/* <AlertNotification
        alertType={alertProps.type}
        message={alertProps.message}
        open={alertProps.open}
        onClose={handleAlertClose}
      /> */}

      {/* Include the loader component */}
      <PageLoader isPageLoading={isPageLoading} />

      {/* Include the Confirmation Dialog component */}
      <ConfirmationDialog
        openConfirmation={confirmDialogOpen}
        dialogContent={confirmationDialogContent}
        onCloseConfirmation={handleCloseConfirmDialog}
        onConfirmDialog={(param) => handleDialogConfirmAction(param)}
      />

      {/* Include the Notification Dialog component */}
      <NotificationDialog
        openNotification={notificationDialogOpen}
        dialogContent={notificationDialogContent}
        onCloseNotification={handleCloseNotificationDialog}
        isAutoClose={timerAutoCloseNotificationDialog}
        timerDuration={timerNotificationDialogClose}
      />
    </div>
  );
});

export default NewWalkIn;
